// PageSlot — the shell's single modal layer for opening pages (renamed from
// CanvasPageSlot 2026-07-09 per Nika "use PageSlot for all opening across all
// pages"). Mounted ONCE at the workspace shell root; every surface opens a
// route in it by writing root state — no per-surface variants:
//
//   root._canvasSlot        — the PRIMARY pane's route
//   root._canvasSlotSecond  — the SECOND pane's route (side-by-side split view,
//                             iPad style — 2026-07-12 per Nika)
//   root._slotFullscreen    — edge-to-edge toggle (single-pane only)
//
// ONE geometry for every case (2026-07-12 per Nika "remove edge cases and
// centralize and unify"): a fixed frame above all page chrome, inset X2 left/
// right, and EQUAL live-measured gaps to the navbars — top = --slot-top
// (header-navbar bottom + --nav-gap, ResizeObserver-maintained in app.js) and
// bottom = --slot-bottom (composer top + --nav-gap, same mechanism). The old
// /tickets full-bleed fork is gone.
import { applyMetadata } from '@symbo.ls/helmet'
// Bare shared-library import, not per-file deep functions/*.js subpaths — a
// deep specifier into @symbo-ls/workspace-shared (a restricted/private-
// registry package) cannot resolve through the platform's public importmap
// once this package rides the catalog's workspace-shell eager chunk (same
// fix as workspace/index.js, workspace/app.js — tickets/execution-board.md
// §1). Deferred call wrappers, not an eager `const { X } = _wsShared.functions`
// destructure, so the barrel-cycle boot-bomb this fix class already hit once
// (a5d0a2aa) can't recur here.
import _wsShared from '@symbo-ls/workspace-shared'
const canvasStripWorkspacePrefix = (...args) => _wsShared.functions.stripWorkspacePrefix(...args)
const shortSlotTitle = (...args) => _wsShared.functions.shortSlotTitle(...args)

// Shared title resolution accepts both metadata factories and title
// factories, at every read. Resolving eagerly would freeze the language;
// reading `.metadata.title` directly loses a metadata factory entirely.
const canvasFeatureEnabled = (...args) => _wsShared.functions.featureEnabled(...args)
const canvasCleanHref = (...args) => _wsShared.functions.cleanHref(...args)
const hasMinimizedEntry = (...args) => _wsShared.functions.hasMinimizedEntry(...args)
const minimizeEntry = (...args) => _wsShared.functions.minimizeEntry(...args)
const removeMinimizedEntry = (...args) => _wsShared.functions.removeMinimizedEntry(...args)
const writeMinimizedList = (...args) => _wsShared.functions.writeMinimizedList(...args)
import {
  histBack,
  histCanBack,
  histCanForward,
  histCurrent,
  histForward,
  histRecord,
  histReplace
} from '../functions/slotHistory.js'
import { slotPageFillProps } from '../functions/slotPageFill.js'
import {
  slotPinAfterPrimaryLeaves,
  slotPinAfterSecondLeaves,
  slotPinToggle
} from '../functions/slotPin.js'

// NOTE: there is deliberately NO per-route override map here anymore (the old
// _CANVAS_SLOT_PAGES = { '/marketplace': marketplace } short-circuit — removed
// tickets/workspace.md "Marketplace PageSlot renders empty content"). It
// bare-mounted canvas/pages/marketplace.js's raw def (`MarketplaceIsland: {}`,
// no `extends`) directly via `el.set`, bypassing the shell's lazy context-fold
// entirely. That was safe back when this component lived inside the CANVAS
// app (canvas/sharedLibraries.js eager-imports the full marketplace context —
// a3e9ab33), but PageSlot moved to the WORKSPACE SHELL root in 94c7b9dd, whose
// own sharedLibraries.js never eager-merges marketplace (only the eager
// install-modal slice does — MarketplaceInstallModal/IntegrationLogo, see
// workspace/context.js). Once `/marketplace` became a properly deferred
// `lazySharedLib` route in workspace/pages/index.js (#16 bundle-slimming),
// `el.context.pages['/marketplace']` started carrying the CORRECT
// context-folding entry — but this override map still won the resolution
// race in `_defFor` below, every time, so that fold never ran: `el.set()`
// mounted `MarketplaceIsland: {}` bare against a context that never learned
// what `MarketplaceIsland` (or any of its children/functions) is → 0
// children, 0 network requests, no marketplace state — exactly the reported
// symptom. Every other lazy sub-app route (/preview, /infra, /documents,
// /files, /chat, /tickets, …) already opens correctly through the plain
// `el.context.pages` fallthrough below with no override — marketplace now
// does too. canvas/pages/marketplace.js itself is untouched and still used
// by canvas's OWN route registry (canvas/pages/index.js), where canvas's
// eager marketplace context makes it resolve correctly on its own.
const _defFor = (el, path, root) => {
  const clean = canvasStripWorkspacePrefix(canvasCleanHref(path))
  if (!clean) return null
  // Resolution order:
  //   1. root._aiAnswerPages — transient AI answer views (render_answer_ui):
  //      defs the assistant drew live for this session, keyed '/answer/<slug>'.
  //      Session-only by design — never persisted, never in the router registry.
  //   2. root._slotResolve — the resolver the HOME installs (`_appPageFor`:
  //      workspaceProject.pages + the lazy `_APP_WINDOW_PAGES` map, then the
  //      rest of context.pages, so the ?page= URL sync below names every
  //      route this chain mounts). This carries
  //      the home's divergent choices the shared router registry lacks. Absent
  //      on non-home surfaces, so /studio /infra /tickets fall straight through.
  //   3. root._modulePagesLiveGet() — the LIVE, correctly design-system-wrapped
  //      module page map (workspaceProjects.js _wrapModulePages/_installLiveGetter).
  //      Installed by the MODULE LOADER itself, not by the home page, so — unlike
  //      _slotResolve — it survives on every surface a module route can be split
  //      from. A MODULE RECORD ROUTE (moduleRecordRoutes registry — a citation's
  //      "Open in split pane") is registered ONLY here and in workspaceProject
  //      .pages (which _slotResolve already covers when present); without this
  //      tier, a split opened while _slotResolve is absent fell straight through
  //      to the static table below, which a dynamically-installed module route
  //      is never in — the second pane mounted its header chrome and no body
  //      (WS-SPLIT-SECOND-PANE-MODULE-ROUTE-RENDERS-HEADER-ONLY-1).
  //   4. el.context.pages — the shell's merged registry (already lazy via
  //      lazyPage/lazySharedLib, already carries activated workspace-project
  //      pages). This is the ONLY path for /marketplace — its lazySharedLib
  //      entry (workspace/pages/index.js) folds the marketplace context into
  //      the live shell registry on first mount, THEN el.sets the real page.
  const liveModulePages =
    root && typeof root._modulePagesLiveGet === 'function' ? root._modulePagesLiveGet() : null
  return (
    (root && root._aiAnswerPages && root._aiAnswerPages[clean]) ||
    (root && root._slotResolve && root._slotResolve(clean)) ||
    (liveModulePages && liveModulePages[clean]) ||
    el.context?.pages?.[clean] ||
    null
  )
}

// Top-level section of a path ('/tickets/board' → 'tickets'). Used to detect
// a nav-away: when the live route leaves the section the slot was opened in,
// the slot is stale and must be dismissed.
const _topSection = (path) =>
  canvasStripWorkspacePrefix(String(path || ''))
    .split('?')[0]
    .split('/')
    .filter(Boolean)[0] || ''

// ── Per-pane navigation state (2026-08-06 per Nika "when we navigate accross
// pageslots - we shuld also support back and forward navigations in
// pageslots… also support sidebar view in pageslot thats quick access for all
// available pageslot apps") ────────────────────────────────────────────────
//
// Each pane owns an independent history + apps-rail flag, keyed off its slot
// key — pane-scoped by construction, so neither surface can ever move the
// sibling pane (the split-view contract). Keys are declared in
// workspace/state-app.js; on roots that don't declare them (canvas
// standalone) the consumers below quietly no-op.
const _HIST_KEYS = { _canvasSlot: '_slotHistPrimary', _canvasSlotSecond: '_slotHistSecond' }
const _RAIL_KEYS = { _canvasSlot: '_slotRailPrimary', _canvasSlotSecond: '_slotRailSecond' }

// Record the pane's live path into its per-pane history. Runs from the pane
// Slot's lifecycle on every SLOT_KEY change, whoever wrote it (openAppPage,
// openInSlot, the rail, tickets' in-canvas writers, …) — the ONE choke point
// that sees them all. A back/forward landing needs no guard flag: the arrows
// write the path the cursor already points at, so histRecord returns the same
// reference and nothing is written (which also terminates the
// record→update→record echo). Skips roots that don't declare the key — smbls
// drops writes to undeclared root keys, so recording there would re-attempt
// an update on every render forever. An in-place query patch (see
// `_inPlaceQueryPatch`) REPLACES the cursor entry instead of adding one, in
// both panes: the pane's Back arrow must never land on a link param the page
// already consumed (lane RQ-2).
const _recordSlotHist = (s, SLOT_KEY, el) => {
  const root = s.root
  const histKey = _HIST_KEYS[SLOT_KEY]
  if (!root || !histKey || root[histKey] === undefined) return
  const path = root[SLOT_KEY]
  if (!path) return
  const prev = root[histKey]
  const next = _inPlaceQueryPatch(el, path, histCurrent(prev))
    ? histReplace(prev, path)
    : histRecord(prev, path)
  if (next !== prev) root.update({ [histKey]: next })
}

// THE PANE'S LIVE PAGE, for the shell (Nika 2026-10-10, "Ask before
// replacing"): a split replaces the pane opposite the caller, and the shell's
// openAppPage asks `hasUnsavedInput` about the page that pane SHOWS before it
// does. Only the host's own display gate knows that it is the shown page, so
// it records itself here — `root.__slotLiveHosts[<pane key>]`, a plain
// non-reactive write (same as `__headerSearchReturnFocusTo`), never an
// update. Shown → recorded; hidden → cleared, but only while it is still the
// recorded host, so the order two hosts re-evaluate in never matters. A parked
// keep-alive host is never recorded: a replace does not lose it.
//
// KNOWN GAP (accepted, ws crm 2026-10-11): an org/workspace switch rebuilds
// the root with `root.replace({ ...root.parse(), … })`
// (workspace functions/loadPrivateData.js), and `parse()` drops `__` keys, so
// the record is EMPTY until a host's display gate runs again. In that window a
// split replaces the target pane at once, without the ask. A stale entry (a
// host swept while recorded) never asks: a removed host has no `__ref`, and
// hasUnsavedInput answers false for it.
export const _markLiveHost = (root, SLOT_KEY, host, live) => {
  if (!root || typeof root !== 'object' || !SLOT_KEY) return
  const map = root.__slotLiveHosts || (root.__slotLiveHosts = {})
  if (live) map[SLOT_KEY] = host
  else if (map[SLOT_KEY] === host) delete map[SLOT_KEY]
}

// Nearest ancestor pane element — panes stamp `_slotPaneKey` (see _slotPane).
// Replaces the old fixed `el.parent?.parent` reach, which broke the moment
// the pane grew its content row (rail + mount target live one level deeper).
const _paneOf = (el) => {
  let n = el && el.parent
  let hops = 0
  while (n && hops < 12) {
    if (n._slotPaneKey) return n
    if (n.parent === n) break
    n = n.parent
    hops++
  }
  return null
}

// ── Minimize-to-dock (2026-08-06 per Nika "we should support minimize
// feature for apps, to continue exactly where it left off") ────────────────
//
// Mechanism — KEEP-ALIVE HOSTS: the Slot no longer mounts pages via a single
// replaced `content` child. Each opened route gets its own host child
// (`SlotKeep_<route>`), created imperatively through the same
// `context.__create` path `el.set()` uses, with the page def `el.set` INTO
// the host. The host is gated by a reactive `display:` predicate — NEVER
// `hide:` and never `if:` — so a minimized app's element subtree (DOM nodes,
// component state, timers, in-flight work) stays fully mounted and alive
// while invisible; restoring flips the predicate back and reveals the SAME
// element instance. Unmounting is explicit (`host.remove()` → full dispose)
// and happens only in `_sweepHosts`.
//
// With minimize as the explicit "keep it alive" verb, a plain CLOSE is now a
// REAL close: the swept host is fully disposed (the pre-minimize builds kept
// the last route's `el.set` content mounted forever as an accident of the
// single-mount design — that implicit keep is deliberately retired).
//
// Ownership of `root._minimizedSlots` mutations (state-app.js declares the
// key; it is in WORKSPACE_SCOPED_KEYS so the switch wipe resets it):
//   • _minimizeSlot (MinimizeBtn) — parks the open primary route (cap 5,
//     oldest evicted; the sweep unmounts evicted hosts).
//   • _adoptActive — drops a route from the list the moment it becomes the
//     ACTIVE slot again (restore-by-any-path: dock chip, launcher, search
//     all just set `_canvasSlot` and converge here), atomically with the
//     activation so the sweep can never see a restored route as closeable.
//   • MinimizedDock's DismissBtn — explicit close of a parked app.
// Every mutation persists the LIST (never the hosts) per-workspace via
// sessionStorage (shared/functions/minimizedSlots.js) so a reload offers the
// same apps as COLD restores (fresh mount, best-effort continuation).
//
// Workspace isolation (house law): hosts are stamped with the workspace id
// they mounted under; the sweep removes any host whose stamp no longer
// matches the live workspace — so neither the kept-alive DOM nor the dock
// list survives an org/workspace switch.
const _canvasWsIdOf = (root) =>
  root?.activeWorkspaceId || root?.workspace?.id || root?.jwtClaims?.active_workspace_id || null

const _HOST_PREFIX = 'SlotKeep_'
const _hostKeyFor = (clean) => _HOST_PREFIX + String(clean || '').replace(/[^A-Za-z0-9]/g, '_')

// AN IN-PLACE QUERY PATCH IS NOT A NAVIGATION (lane RQ-2, the in-place half
// of WS-MODULE-ROUTE-QUERY-NO-HISTORY-ENTRY-1). A module page that consumes a
// link param (Catalog opens its New product form from `?new=1&name=`, then
// clears both) moves its pane route through the shell's
// `setModuleRouteQuery`, which stamps the live host with the new full route
// BEFORE it writes the slot key, so `_mountPane` keeps the page (keep-alive
// hit, no re-mount). Such a write REPLACES the pane's current entry, in the
// address bar (`_writeSlotUrl` without push) and in the pane history
// (`_recordSlotHist`). An added entry is one Back lands on: `_mountPane` then
// meets a route the host does not carry, re-mounts, and the consumed form
// opens a second time.
//
// In place = the same clean route as `prev`, AND the route's own keep-alive
// host already carries this exact path. A skeleton host never carries
// `_slotPath` (_showSkeleton), a deep-link change (room → board) arrives
// before any stamp, and a keep-alive re-open changes the clean route: all
// three stay navigations and push. A family route has no per-route host (one
// host per family, whose Body re-mounts on every route change), so it never
// reads as in place.
const _inPlaceQueryPatch = (el, path, prev) => {
  if (!el || typeof path !== 'string' || typeof prev !== 'string') return false
  if (!path || path === prev) return false
  const clean = canvasCleanHref(path)
  if (!clean || clean !== canvasCleanHref(prev)) return false
  const host = el[_hostKeyFor(clean)]
  return !!(host && host.__ref && host._slotPath === path)
}

// ── Family hosts (2026-08-14, Nika-approved "when switching tabs we can
// partially update ui" — scope 2: shell-wide slot reuse) ────────────────────
//
// Same-FAMILY routes share ONE keep-alive host with a stable two-part
// skeleton: `Chrome` (the family's shared header/controls — mounted once,
// never touched again) and `Body` (the page def — the ONLY thing that swaps
// on an intra-family route change). Chrome DOM nodes keep their identity
// across the switch, so the tab/chip strip never flashes or remounts.
//
// Families are declared by the HOME via `root._slotFamilyFor(clean)` →
// `{ id, chrome }` or null (workspace/pages/main.js, installed beside
// `_slotResolve`). Absent resolver (canvas standalone) or unfamilied route
// → the per-route host path below, byte-for-byte today's behavior.
//
// Known, accepted degradation: parking a family route in the dock and then
// opening a SIBLING route of the same family swaps the shared Body — the
// parked entry falls back to a cold restore (the dock list survives; the
// live subtree doesn't). The dock contract stays intact for everything
// outside the family's own sibling set.
const _familyOf = (root, clean) => {
  const fn = root && root._slotFamilyFor
  if (typeof fn !== 'function' || !clean) return null
  try {
    return fn(clean) || null
  } catch {
    return null
  }
}
const _familyHostKey = (id) => _HOST_PREFIX + 'family_' + String(id).replace(/[^A-Za-z0-9]/g, '_')

const _familyHostDef = (family, SLOT_KEY) => ({
  // Same reactive-`display:` keep-alive gate as _hostDef, keyed on the
  // FAMILY of the active route instead of route identity.
  display: (el, s) => {
    void s.root?._slotNudge
    const fam = _familyOf(s.root, canvasCleanHref(s.root?.[SLOT_KEY]))
    const live = !!(fam && fam.id === family.id)
    _markLiveHost(s.root, SLOT_KEY, el, live)
    if (live) return 'flex'
    const pane = _paneOf(el)
    if (pane && pane._closing) {
      const lastFam = _familyOf(s.root, canvasCleanHref(el.parent?._lastSlot))
      if (lastFam && lastFam.id === family.id) return 'flex'
    }
    return 'none'
  },
  subscribeTo: [SLOT_KEY],
  flex: '1 1 0',
  minWidth: '0',
  minHeight: '0',
  width: '100%',
  flexDirection: 'column',
  overflow: 'hidden',
  // Chrome — set ONCE at first family mount (with the page def's context so
  // the family package's bare keys resolve); persists across Body swaps.
  Chrome: { flex: '0 0 auto', width: '100%' },
  // Body — the swap target. Owns the scroll so the chrome never moves.
  Body: {
    flex: '1 1 0',
    minWidth: '0',
    minHeight: '0',
    width: '100%',
    display: 'flex',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    '& > [data-key]': {
      flex: '1 1 0',
      minWidth: '0',
      minHeight: '0',
      maxWidth: '100%',
      // ONE ground ("keep one bg", 2026-09-01): the page ROOT this host
      // wraps goes transparent by specificity (0,2,0 beats its atomic
      // ground class), so the pane's stage-chain ground is the only sheet.
      background: 'transparent'
    }
  }
})

// Host wrapper def. The page def is `el.set` into it (same stripping the old
// single-mount used). The host is the Slot's flex item (the Slot is a flex
// box the size of the pane's content row — see the Slot notes below): it
// takes the Slot's `& > [data-key]` sizing and passes the same lever down to
// the page root it hosts.
const _hostDef = (clean, SLOT_KEY) => ({
  // KEEP-ALIVE GATE — reactive `display:` (the banked rule: `hide:` would
  // stop re-evaluating once the subtree is out; `if:` would unmount it).
  // Reads `_slotNudge` for the same reason the pane's hide factory does: a
  // pane holds visible through its exit animation via element-local
  // `_closing`, which no signal tracks — the close-finish nudge re-runs this
  // so a minimized route keeps painting INSIDE the pane while it fades out,
  // then goes display:none when the choreography lands.
  display: (el, s) => {
    void s.root?._slotNudge
    const live = canvasCleanHref(s.root?.[SLOT_KEY]) === clean
    _markLiveHost(s.root, SLOT_KEY, el, live)
    if (live) return 'flex'
    const pane = _paneOf(el)
    if (pane && pane._closing && canvasCleanHref(el.parent?._lastSlot) === clean) return 'flex'
    return 'none'
  },
  subscribeTo: [SLOT_KEY],
  // Counts changes inside the page (both events bubble here), so a page the
  // user chose to Keep is asked about again only after it changed again
  // (_confirmDiscardParked).
  onInput: (e, el) => _recordChange(e, el),
  onChange: (e, el) => _recordChange(e, el),
  flex: '1 1 0',
  minWidth: '0',
  minHeight: '0',
  width: '100%',
  overflow: 'auto',
  overscrollBehavior: 'contain',
  // THE HOST SCROLLS, SO IT IS NEVER A STACKING CONTEXT (DESIGN-SHELL-
  // RESPONSIVE-IOS-SAFARI-1, the iOS fixed-layer rule): iOS Safari clips
  // every `position: fixed` layer inside a scroller that is a stacking
  // context to the scroller's box — a module page's fixed sheet, dialog or
  // toast would be cut to the pane, an empty band in its place (found on the
  // iOS 26 Simulator by the public lane: the app root's z-index 0 hid the
  // Ask bar). Desktop WebKit and Chromium never show it. No z-index,
  // isolation, transform, filter, opacity, will-change or paint containment
  // here. WS-SLOT-HOST-ISOLATES-STACKING-1's isolation (a page's z 20/30
  // layers must stay under the apps rail at z 3) lives one level up, on the
  // pane's Slot, which does not scroll.
  '& > [data-key]': {
    flex: '1 1 0',
    minWidth: '0',
    minHeight: '0',
    maxWidth: '100%',
    // ONE ground ("keep one bg", 2026-09-01): the page ROOT this host
    // wraps goes transparent by specificity (0,2,0 beats its atomic
    // ground class), so the pane's stage-chain ground is the only sheet.
    background: 'transparent'
  }
})

// Test seams (__tests__/pageSlotHostIsolatesStacking.test.js,
// workspace/__tests__/iosFixedLayerScrollers.test.js).
export const __hostDefForTests = _hostDef
export const __familyHostDefForTests = _familyHostDef

// Remove every host that is neither the ACTIVE route, nor PARKED in
// `_minimizedSlots` (primary pane only — the split pane never owns parked
// entries), nor stamped for the CURRENT workspace. Runs from the Slot's
// lifecycle on every relevant signal; deferred while the pane's exit
// choreography holds (`_closing`) so a closing sheet never blanks mid-fade —
// the close-finish `_slotNudge` re-fires the Slot and the sweep lands then.
// ── MODULE KEEP-ALIVE (WS-PAGE-SWITCH-SLOW-1, 2026-08-18) ─────────────────
// Measured on the bellforge module (dev shell, hidden-tab lower bounds —
// checkpoint 2 on tickets/opus.md): every intra-module switch DISPOSED the
// previous route's host and re-prepared the next def from scratch — /theses
// 1602 → 1283 → 1986 ms across three visits (revisits pay FULL price), plus
// one data refetch per switch. The reveal path in _mountPane ("Keep-alive
// hit — reveal only") already makes a SURVIVING host free to revisit; the
// whole fix is letting module-route hosts survive the sweep while the user
// stays inside the same module.
//
// WHY DROPPING THE PER-SWITCH DISPOSE IS SAFE — AND WHAT IT WAS DOING.
// The dispose was also BOUNDING a leak: heap grew ~6 MB per switch even
// WITH disposal, so whatever retains per-mount memory was only being
// truncated by the sweep, never fixed. Removing the dispose removes that
// accidental bound, so the keep is CAPPED: at most SLOT_MODULE_KEEP_CAP
// parked module hosts, LRU by `_slotSeenAt` (the ACTIVE route's host is
// never evicted). Membership is decided against the CURRENT live module
// page map (`root._modulePagesLiveGet`, installed by workspaceProjects'
// module loader), so a module or workspace switch naturally evicts every
// host the old module parked — outside a module (getter absent: canvas
// standalone, plain shell routes) the sweep behaves exactly as before.
// ⚠ Heap over the same 5-switch protocol MUST be re-measured beside the
// timings whenever this block changes (tickets/opus.md acceptance).
//
// perf/shell-keepalive (2026-10-10): 6 → 3. Measured on staging
// /w/vault/default: the seven VaultOS pages all stayed mounted (display:none)
// once visited — 9,489 DOM nodes, VaultSchema alone 1,797 and the training
// page 1,310. The kept-alive set is now the ACTIVE page + the 3 most recently
// seen. A parked page is protected ONLY when it DECLARES unsaved work (the
// shell's `hasUnsavedInput`: an `isDirty` hook or a `dirty` flag); such a page
// past the cap is asked about first ("Discard changes?", Keep is the default)
// — see _confirmDiscardParked. No value heuristic, no `keepAlive` pin.
const SLOT_MODULE_KEEP_CAP = 3
const _moduleLiveRoutes = (root) => {
  const get = root && root._modulePagesLiveGet
  if (typeof get !== 'function') return null
  try {
    const map = get()
    return map && typeof map === 'object' ? map : null
  } catch {
    return null
  }
}
// Pure keep/evict plan for module-candidate hosts — exported for the
// bun:test regression (__tests__/pageSlotModuleKeep.test.js,
// __tests__/pageSlotKeepPinned.test.js) so the cap and the eviction order stay
// pinned without a DOM. `hosts` is metadata only:
// [{ key, href, ws, seenAt, pinned, kept }].
//
// `pinned` = the page declares unsaved work. At most `cap` parked hosts stay:
// pinned hosts take those slots first, newest first, the clean ones fill what
// is left, newest first, and the rest are evicted silently. A pinned host past
// the cap goes to `confirm` (the caller asks the user) — unless it is `kept`
// (the user already answered Keep and the page has not changed since): that
// one stays, without a new prompt. A workspace switch or leaving the module
// evicts every parked host without a prompt — tenant isolation wins.
export const _moduleKeepPlan = (
  hosts,
  { activeClean, wsId, liveRoutes, cap = SLOT_MODULE_KEEP_CAP }
) => {
  const keep = []
  const evict = []
  const confirm = []
  const parked = []
  const activeInModule = !!(liveRoutes && activeClean && activeClean in liveRoutes)
  for (const h of hosts) {
    const stale = !!(wsId && h.ws && h.ws !== wsId)
    const inModule = !!(liveRoutes && h.href && h.href in liveRoutes)
    if (!activeInModule || !inModule || stale) {
      evict.push(h)
      continue
    }
    if (h.href === activeClean) {
      keep.push(h)
      continue
    }
    parked.push(h)
  }
  const newest = (a, b) => (b.seenAt || 0) - (a.seenAt || 0)
  const pinned = parked.filter((h) => h.pinned).sort(newest)
  const free = parked.filter((h) => !h.pinned).sort(newest)
  const pinnedKept = pinned.slice(0, cap)
  const budget = Math.max(0, cap - pinnedKept.length)
  keep.push(...pinnedKept, ...free.slice(0, budget))
  for (const h of pinned.slice(cap)) (h.kept ? keep : confirm).push(h)
  evict.push(...free.slice(budget))
  return { keep, evict, confirm }
}

// Does this parked host DECLARE unsaved work? Only the shell's one rule
// answers — `hasUnsavedInput` (an `isDirty` hook or a `dirty` flag anywhere in
// the page; no value heuristic). Canvas standalone (no shell rule registered)
// pins nothing. The sweep asks only for hosts the plan would evict
// (`_planWithPins`), so the page tree is never walked on every sweep.
export const _hostPinned = (host) => {
  if (!host || typeof host !== 'object') return false
  const fns = host.context && host.context.functions
  if (!fns || typeof fns.hasUnsavedInput !== 'function') return false
  return !!host.call('hasUnsavedInput', host)
}

// Plan with pins checked LAZILY (review D7): plan unpinned, check only the
// hosts that plan would evict, re-plan when one turns out pinned. Stale and
// out-of-module hosts go regardless, so they are never checked. `isPinned`
// runs at most once per host. Exported for the bun:test regression.
export const _planWithPins = (hosts, ctx, isPinned) => {
  const known = new Map()
  const activeInModule = !!(ctx.liveRoutes && ctx.activeClean && ctx.activeClean in ctx.liveRoutes)
  const eligible = (h) =>
    activeInModule &&
    !(ctx.wsId && h.ws && h.ws !== ctx.wsId) &&
    !!(ctx.liveRoutes && h.href && h.href in ctx.liveRoutes) &&
    h.href !== ctx.activeClean
  for (;;) {
    const plan = _moduleKeepPlan(
      hosts.map((h) => ({ ...h, pinned: known.get(h.key) === true })),
      ctx
    )
    let changed = false
    for (const h of plan.evict) {
      if (known.has(h.key) || !eligible(h)) continue
      const pinned = !!isPinned(h)
      known.set(h.key, pinned)
      if (pinned) changed = true
    }
    if (!changed) return plan
  }
}

// The host's own `onInput` / `onChange` (both bubble here through DOMQL's
// delegation): one more change inside the page. A Keep answer is tied to the
// change count it was given at (_confirmDiscardParked).
export const _recordChange = (e, host) => {
  if (!host) return
  host._slotChangeSeq = (host._slotChangeSeq || 0) + 1
}

// Has the user already answered Keep for this page, with no change since?
const _keptUnchanged = (host) =>
  !!host && host._slotKeptSeq != null && host._slotKeptSeq === (host._slotChangeSeq || 0)

// "Discard changes?" before a declared-dirty page past the cap is unmounted —
// the shell's ONE prompt, `confirmDiscardChanges` (Keep is its default: the
// Keep button, Esc, the veil and a refused layer all keep). Rules:
//   • one prompt per page while it is open;
//   • after Keep, not asked again until the page changes again;
//   • no prompt while the workspace id is unknown (a switch in flight);
//   • Discard never removes the page that is on screen by then (the user may
//     have gone back to it while the prompt was open — review N5);
//   • without the shell prompt registered (canvas standalone) the page stays.
// Returns true when it asked.
export const _confirmDiscardParked = (slot, host, { wsId, isActive, discard } = {}) => {
  if (!host || host._slotAsking || !wsId || _keptUnchanged(host)) return false
  const fns = slot && slot.context && slot.context.functions
  if (!fns || typeof fns.confirmDiscardChanges !== 'function') return false
  host._slotAsking = true
  slot.call('confirmDiscardChanges', {
    onDiscard: () => {
      host._slotAsking = false
      if (typeof isActive === 'function' && isActive()) return
      if (typeof discard === 'function') discard()
    },
    onKeep: () => {
      host._slotAsking = false
      host._slotKeptSeq = host._slotChangeSeq || 0
    }
  })
  return true
}

const _sweepHosts = (el, s, SLOT_KEY, primary) => {
  const _pane = _paneOf(el)
  if (_pane && _pane._closing) return
  const ref = el.__ref
  if (!ref || !ref.__children) return
  const root = s.root
  const wsId = _canvasWsIdOf(root)
  const activeClean = canvasCleanHref(root?.[SLOT_KEY])
  const minimized = primary && Array.isArray(root?._minimizedSlots) ? root._minimizedSlots : []
  const activeFam = _familyOf(root, activeClean)
  const liveRoutes = _moduleLiveRoutes(root)
  const modCand = []
  for (const key of [...ref.__children]) {
    if (typeof key !== 'string' || !key.startsWith(_HOST_PREFIX)) continue
    const host = el[key]
    if (!host || !host.__ref) continue
    const stale = wsId && host._slotWs && host._slotWs !== wsId
    // Family hosts survive as long as the ACTIVE route — or any PARKED
    // route — belongs to their family; per-route hosts keep the exact
    // route-identity semantics they always had.
    const keep = host._slotFamily
      ? !stale &&
        ((activeFam && activeFam.id === host._slotFamily) ||
          minimized.some((en) => {
            const f = _familyOf(root, canvasCleanHref(en && en.href))
            return f && f.id === host._slotFamily
          }))
      : !stale &&
        (host._slotHref === activeClean ||
          minimized.some((en) => canvasCleanHref(en && en.href) === host._slotHref))
    if (keep) continue
    // MODULE KEEP — a host that lost the route-identity keep may still be a
    // parked module page; defer to the capped plan below. Family hosts keep
    // their own contract and never enter the module plan.
    if (!host._slotFamily && liveRoutes) {
      modCand.push({ key, host })
      continue
    }
    // The in-flight/dedupe guard must not outlive the host it described, or
    // a later same-route open would skip its own mount.
    if (canvasCleanHref(el._lastSlot) === host._slotHref) el._lastSlot = null
    host.remove()
  }
  if (modCand.length) {
    const byKey = new Map(modCand.map((c) => [c.key, c.host]))
    const plan = _planWithPins(
      modCand.map(({ key, host }) => ({
        key,
        href: host._slotHref,
        ws: host._slotWs,
        seenAt: host._slotSeenAt,
        kept: _keptUnchanged(host)
      })),
      { activeClean, wsId, liveRoutes, cap: SLOT_MODULE_KEEP_CAP },
      (h) => _hostPinned(byKey.get(h.key))
    )
    const confirmKeys = new Set(plan.confirm.map((h) => h.key))
    for (const { key, host } of modCand) {
      if (!confirmKeys.has(key)) continue
      _confirmDiscardParked(el, host, {
        wsId,
        // the page on screen by the time the user answers is never removed
        isActive: () => canvasCleanHref(root?.[SLOT_KEY]) === host._slotHref,
        discard: () => {
          if (!host.__ref) return
          if (canvasCleanHref(el._lastSlot) === host._slotHref) el._lastSlot = null
          host.remove()
        }
      })
    }
    const evictKeys = new Set(plan.evict.map((h) => h.key))
    for (const { key, host } of modCand) {
      if (!evictKeys.has(key)) continue
      if (canvasCleanHref(el._lastSlot) === host._slotHref) el._lastSlot = null
      host.remove()
    }
  }
}

// Restore-by-any-path: the moment a parked route becomes the active slot
// again, un-park it. Single owner of the restore-side list edit, running in
// the same update pass that activated the route.
const _adoptActive = (el, s) => {
  const root = s.root
  const list = root?._minimizedSlots
  if (!Array.isArray(list) || !list.length) return
  const activeClean = canvasCleanHref(root?._canvasSlot)
  if (!activeClean || !hasMinimizedEntry(list, activeClean)) return
  const next = removeMinimizedEntry(list, activeClean)
  writeMinimizedList(_canvasWsIdOf(root), next)
  root.update({ _minimizedSlots: next })
}

// Title/icon for a dock chip: the page def's own metadata first, enriched
// from the launcher tile registry when the shell provides it (canvas
// standalone doesn't register it — availability-checked like
// OnlineMembersDock's isActiveMember, never a blind el.call, so the dev
// unresolved-call warning can't fire).
//
// When the title IS the launcher tile's and the tile carries an installed
// app's record key (`appKey` — the shell stamps it on the ONE tile an app
// contributes, workspace functions/workspaceConfigs.js), the key rides on the
// entry too: the chip names the app by it (MinimizedDockChip), the App Store's
// name, where the tile's own title is the app manifest's word ("Store" for
// Commerce). A page's own title has no key — it names the page, not the app.
const _slotEntryMeta = (el, root, clean, def) => {
  let title = shortSlotTitle(el.call('resolvePageMetadataTitle', def?.metadata)) || null
  let icon = def?.metadata?.icon || null
  let appKey = null
  if (!title || !icon) {
    const groupsFn =
      el.context?.functions?.searchAppGroupsFor || el.context?.methods?.searchAppGroupsFor
    if (typeof groupsFn === 'function') {
      try {
        const groups = groupsFn.call(el, root) || []
        for (const g of groups) {
          const tile = (g?.apps || []).find((a) => a && a.route === clean)
          if (tile) {
            if (!title && tile.appKey) appKey = tile.appKey
            title = title || tile.title
            icon = icon || tile.icon
            break
          }
        }
      } catch (_) {
        /* enrichment is best-effort */
      }
    }
  }
  return { title: title || clean, icon: icon || 'layers', ...(appKey ? { appKey } : {}) }
}

// Park the open PRIMARY route: append/refresh its dock entry (cap 5, oldest
// evicted — the sweep unmounts the evicted host), then close the pane with
// the SAME promote semantics the CloseBtn uses — all in ONE root.update so
// the host sweep and the pane choreography see a consistent world.
const _minimizeSlot = (el, root) => {
  const path = root?._canvasSlot
  if (!path) return
  const clean = canvasCleanHref(path)
  const def = _defFor(el, clean, root)
  const wsId = _canvasWsIdOf(root)
  const meta = _slotEntryMeta(el, root, clean, def)
  const entry = {
    href: path,
    title: meta.title,
    icon: meta.icon,
    ...(meta.appKey ? { appKey: meta.appKey } : {}),
    wsId,
    minimizedAt: Date.now()
  }
  const { list } = minimizeEntry(root._minimizedSlots || [], entry)
  writeMinimizedList(wsId, list)
  root.update({
    _slotPinned: slotPinAfterPrimaryLeaves(root),
    _minimizedSlots: list,
    _canvasSlot: root._canvasSlotSecond || null,
    _canvasSlotSecond: null,
    // null, not false — closes and minimizes reset to "no explicit choice"
    // so the next open re-resolves pref → module default (B1 tri-state,
    // slotFullscreenActive in the shell's workspaceProjects.js).
    _slotFullscreen: null,
    _slotHistSecond: null,
    _slotRailPrimary: false,
    _slotRailSecond: false
  })
}

// Close the slot wholesale — the ONE place the "always reset fullscreen"
// invariant lives. `promote: true` implements the iPad semantics: closing the
// primary hands its frame to the surviving second pane.
//
// Navigation-state policy on close: both apps rails reset (an open rail must
// never greet the next open), the SECOND pane's history dies with its pane
// (the next split is a fresh lineage), and the PRIMARY's history survives —
// re-opening the slot keeps Back working across the close, the same
// continuity contract minimize-to-dock set. On promote, the recorder files
// the promoted route into the primary history, so Back returns to the app
// the primary showed before the close.
const _closeSlot = (root, opts) => {
  if (!root) return
  if (opts && opts.promote && root._canvasSlotSecond) {
    root.update({
      // the pin follows the right pane into the left place (functions/slotPin.js)
      _slotPinned: slotPinAfterPrimaryLeaves(root),
      _canvasSlot: root._canvasSlotSecond,
      _canvasSlotSecond: null,
      _slotFullscreen: null,
      _slotHistSecond: null,
      _slotRailPrimary: false,
      _slotRailSecond: false
    })
    return
  }
  root.update({
    _canvasSlot: null,
    _canvasSlotSecond: null,
    // null (tri-state) — "always reset fullscreen" now means "always drop
    // back to the resolved default", not "always windowed" (B1).
    _slotFullscreen: null,
    _slotHistSecond: null,
    _slotRailPrimary: false,
    _slotRailSecond: false,
    _slotPinned: null
  })
}

// The ONE dismissal a pane has, whichever pane it is: the primary promotes a
// surviving split pane through _closeSlot (iPad semantics), the split pane
// clears itself AND any in-progress split intent. Extracted from the header
// CloseBtn so the phone drag-to-dismiss gesture below can route through the
// EXACT same path instead of inventing a second close (which would miss the
// promotion, the fullscreen reset and the ?page= URL sync hanging off it).
const _dismissPane = (root, primary) => {
  if (!root) return
  if (primary) {
    _closeSlot(root, { promote: true })
    return
  }
  root.update({
    _canvasSlotSecond: null,
    _slotSplitIntent: false,
    _slotHistSecond: null,
    _slotRailSecond: false,
    _slotPinned: slotPinAfterSecondLeaves(root)
  })
}

// Persist the open PRIMARY slot as ?page=<route> so a reload restores it — but
// ONLY on the home route ('/'), where the URL would otherwise stay '/' and lose
// the open sub-app. Other surfaces open the slot from within a real route whose
// own URL already identifies it. An opening/changing route pushes (Back steps
// back through it); a close or a same-URL re-render replaces — see
// `_writeSlotUrl`'s own comment. The home onCreate reads ?page= back on load
// (workspaceProjects.js re-applies it once a workspace-module's pages exist).
// The split pane is transient — not persisted.
const SLOT_URL_PARAM = 'page'
// The query a slot ROUTE carries, as key/value pairs. A module opens one of
// its own records by passing the params that identify it
// (`/vault-rooms?tenant=longbow&item=<id>`), and those params must reach
// `location.search` — a module page reads them there, and that is the ONLY
// way a slot open and a cold load of the same route look identical to it
// (A1-06). Nesting them inside the `?page=` value is not enough: nothing
// reads a query-inside-a-query.
const _slotRouteQuery = (routeStr) => {
  const qs = String(routeStr || '').split('?')[1]
  if (!qs) return []
  return [...new URLSearchParams(qs.split('#')[0]).entries()].filter(
    ([k]) => k && k !== SLOT_URL_PARAM
  )
}
// The pure half of the URL sync: given the current href, the slot route and
// whether that route is trackable, return the next `pathname+search+hash`
// (or null when nothing should change). Exported for tests — same reason
// `_moduleKeepPlan` is.
export const _slotUrlNext = (href, path, trackable) => {
  const url = new URL(href)
  const hadParam = url.searchParams.has(SLOT_URL_PARAM)
  if (!trackable && !hadParam) return null
  // Retire the params mirrored for the PREVIOUS slot route before writing
  // the new ones. The outgoing `?page=` value is the exact record of what
  // this function put there last time, so the cleanup needs no extra state
  // and can never delete a param some other surface owns.
  for (const [k] of _slotRouteQuery(url.searchParams.get(SLOT_URL_PARAM))) {
    url.searchParams.delete(k)
  }
  if (trackable) {
    url.searchParams.set(SLOT_URL_PARAM, path)
    for (const [k, v] of _slotRouteQuery(path)) url.searchParams.set(k, v)
  } else url.searchParams.delete(SLOT_URL_PARAM)
  return url.pathname + url.search + url.hash
}
// `opts.push` (WS-MODULE-ROUTE-QUERY-NO-HISTORY-ENTRY-1): the onUpdate call
// site asks for pushState when the pane is opening or changing to a new
// route — a room, a board, an item — so Back steps back through them one at
// a time. The onCreate mount-reaffirm never passes it (a cold-load reaffirm
// is not a user navigation) and a close (path falsy) always replaces —
// never push an entry for the thing the user just explicitly closed. The
// NO-OP guard below still gates BOTH: a push only ever happens when the URL
// is actually changing.
export const _writeSlotUrl = (path, root, opts) => {
  if (typeof window === 'undefined') return
  const onHome = canvasStripWorkspacePrefix(root?.currentPath || window.location.pathname) === '/'
  try {
    const clean = path && path.split('?')[0]
    const trackable = onHome && !!clean && !!(root && root._slotResolve && root._slotResolve(clean))
    const next = _slotUrlNext(window.location.href, path, trackable)
    if (next === null) return
    // NO-OP writes must not touch history: this runs from the slot's reactive
    // render pass, so with a slot open it re-fires on every root update. The
    // AppShell history patch dispatches `locationchange` on EVERY
    // replaceState — identical URL included — and the shell's
    // _closeOnNav listener closes any open dropdown layer on that event.
    // Without this guard, no dropdown could stay open while a PageSlot was
    // open: the search/apps launcher opened and was closed by the very next
    // slot render (~200ms), measured live 2026-08-06 (the "searchdropdown
    // is not opening when pageslot is open" bug — stack: _writeSlotUrl →
    // patched replaceState → locationchange → _closeOnNav → closeDropdown).
    const current = window.location.pathname + window.location.search + window.location.hash
    if (next === current) return
    if (opts && opts.push && path) window.history.pushState(window.history.state, '', next)
    else window.history.replaceState(window.history.state, '', next)
  } catch (err) {
    console.warn('[PageSlot] url sync failed:', err)
  }
}

// Native Back/Forward rewrites the address bar directly — `_writeSlotUrl`
// above is state -> URL only, so nothing told `_canvasSlot` the URL just
// moved. Left alone, the very next render pass's `_writeSlotUrl` call sees
// the STALE in-memory route and no-ops — Back would visibly change the
// address bar and then leave the sheet showing the old content, or a later
// render would silently push a fresh entry over the one the user just
// landed on. This is the paired read half that makes the pushState above
// safe (WS-MODULE-ROUTE-QUERY-NO-HISTORY-ENTRY-1). Home-only, same scope
// `_writeSlotUrl` writes to: off-home routes identify the slot by the live
// pathname itself (the module's own route), not `?page=`, and
// `_dismissStaleSlot`'s section watch already retires a stale slot there.
export const _syncSlotFromUrl = (root) => {
  if (typeof window === 'undefined' || !root) return
  const onHome = canvasStripWorkspacePrefix(root.currentPath || window.location.pathname) === '/'
  if (!onHome) return
  const pageParam = new URLSearchParams(window.location.search).get(SLOT_URL_PARAM)
  const clean = pageParam && pageParam.split('?')[0]
  const trackable = !!clean && !!(root._slotResolve && root._slotResolve(clean))
  if (trackable) {
    if (root._canvasSlot !== pageParam) root.update({ _canvasSlot: pageParam })
    return
  }
  // No (or an unresolvable) `?page=` — popped past every open into the bare
  // grid. Full close, the same reset the X button uses, so nothing survives
  // half-open (fullscreen, the split pane, the rail flags).
  if (root._canvasSlot || root._canvasSlotSecond) _closeSlot(root)
}

// The tab title belongs to the PRIMARY pane while any slot is open. Helmet
// only writes on a fresh page mount, which leaves two stale-title holes the
// keep-alive design creates: (1) a same-route re-open skips the mount, so
// nothing re-writes the title the close handed back to the owning page;
// (2) a second-pane mount writes ITS title, which must revert to the
// primary's when that pane closes (or immediately after it mounts — the
// primary owns the URL, so it owns the title).
const _syncSlotTitle = (el, root) => {
  if (typeof document === 'undefined' || !root || !root._canvasSlot) return
  const def = _defFor(el, root._canvasSlot, root)
  const title = el.call('resolvePageMetadataTitle', def?.metadata)
  if (title && document.title !== title) applyMetadata({ title }, document)
}

// The router keeps its live page under this key. Capture the ELEMENT when
// a primary pane opens, not its title: metadata must resolve with that page's
// local state and the current language/brand when the pane closes.
const _routeTitleOwner = (el) => {
  const routed = el.getRoot?.()
  return routed?.[routed.__ref?.contentElementKey || 'content'] || null
}

const _restoreRouteTitle = (el) => {
  const owner = el.__slotTitleOwner
  el.__slotTitleOwner = null
  // Expand/full-page navigation may already have replaced the underlying
  // route. Its new Helmet owns the title; never overwrite it with the page
  // that happened to sit behind the pane before navigation.
  if (!owner || owner !== _routeTitleOwner(el) || typeof owner.call !== 'function') return
  const title = owner.call('resolvePageMetadataTitle', owner.metadata)
  const doc = owner.context?.document || (typeof document !== 'undefined' && document)
  if (title && doc && doc.title !== title) applyMetadata({ title }, doc)
}

// Clear a stale slot when the user navigates away from the section that opened
// it (ticket 6a32d411 — the overlay lingered after navigating out of the
// owning section). Record the route present when the slot opened
// (el._slotRoute) and dismiss BOTH panes once currentPath's top section
// differs. Re-opening on the SAME section never clears.
const _dismissStaleSlot = (el, s) => {
  const root = s.root
  if (!root) return
  const route = root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '')
  if (!root._canvasSlot && !root._canvasSlotSecond) {
    // A split intent with BOTH panes empty is always stale: the split can
    // only be armed from an open slot's header, so nothing legitimate
    // arrives here with the intent still raised. Left set, it kept the
    // empty "New pane / Choose an app for this pane" placeholders painted
    // over the page after a slot-page navigation (investor audit
    // 2026-08-14: ?page=/admin → ?page=/overview stranded two pickers).
    if (root._slotSplitIntent) root.update({ _slotSplitIntent: false })
    el._slotRoute = null
    return
  }
  if (el._slotRoute == null) {
    el._slotRoute = route
    return
  }
  if (_topSection(route) === _topSection(el._slotRoute)) return
  el._slotRoute = null
  _closeSlot(root)
}

// ── Open/close choreography ────────────────────────────────────────────────
//
// Driven IMPERATIVELY via the Web Animations API, for three verified reasons:
// (1) the entrance must be armed BEFORE the first visible paint (the no-blink
// inline-opacity hold below) and WAAPI gives explicit control of that moment;
// (2) the exit needs finish/cancel hooks to hold the pane through the
// animation and then land the keep-alive display:none — declarative CSS can't
// signal completion into the close choreography (and el.update({}) can't
// re-apply `hide` afterwards — see the OPEN el.update({}) ticket in
// tickets/smbls.md); (3) hide-driven display flips re-attach nodes, which
// restarts declarative animations from frame 0 (the closed animation-restart
// forensics in tickets/smbls.md concede exactly the node re-create case).
// `node.animate()` is one-shot and owned here, immune to all of it.
//
// iOS feel (2026-07-12 per Nika "smooth like iOS"): Apple's sheet curve
// cubic-bezier(0.32, 0.72, 0, 1), a gentle rise + settle in, a quick drop out.
//
// ONE RECIPE (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1 — DESIGN_SPEC §6.4 "Center
// sheet", HOME_AMBIENT_SKIN_SPEC §6): every open rises the same way — opacity
// 0 → 1 with a 2.5 % rise and a 0.97 settle, timing `D` on `springBezier`,
// grown from the sheet's bottom edge (it sits on the dock) — and every close
// is the same keyframes back, one letter faster (`C`) on `defaultBezier`. The
// iPad zoom-from-the-tapped-icon entrance is retired: a home widget, the
// search, a notification and a deep link now open ONE sheet the ONE way, so
// the sheet never lands from a different place depending on who asked.
//
// WAAPI takes numbers, not tokens, so the literals below ARE the tokens: the
// brand timing sequence is base 150 ms × the perfect fourth (1.333) per
// letter — C = 266.5 ms, D = 355.3 ms — and the two curves are the brand's
// springBezier / defaultBezier. __tests__/pageSlotSheetMotion.test.js locks
// every number to the brand token it stands for (and
// pageSlotOverlayLanguage.test.js keeps its lock on `_IOS_EASE`).
//
// NO-BLINK contract: the pane is pre-hidden with an INLINE opacity 0 in the
// same synchronous effect that flips it visible (before the browser paints),
// so it can never flash fully-opaque before the entrance starts.
//
// FRAME-INDEPENDENCE contract (2026-07-12, root cause of "opening sometimes
// does not work"): WAAPI finish/cancel events are dispatched by the "update
// animations and send events" step of RENDERING — a document that produces no
// frames (hidden tab, occluded window, starved main thread) NEVER fires them.
// Verified live: a ?page= restore in a background tab left the pane display:
// flex with the inline pre-hide stuck at 0 — the old stuck-guard cancelled the
// play-pending entrance and its cleanup was itself hostage to the frame-gated
// oncancel. State said "open", so same-route clicks deduped to nothing: the
// slot looked dead until a close healed it. Therefore correctness NEVER rides
// on animation events here:
//   • every run is token-owned (el._runSeq) — starting a new run detaches the
//     old run's handlers, cancels its animation + timer, so no stale callback
//     can act on a superseded state;
//   • every run arms a plain setTimeout backstop that settles the choreography
//     directly (timers fire — merely clamped — where animation events don't);
//     onfinish is only the fast path;
//   • a document that isn't rendering (visibilityState 'hidden') or prefers
//     reduced motion skips animation entirely and lands the final state
//     synchronously — animating for nobody is pure risk;
//   • the hide factory self-heals: an open pane with no run in flight is
//     asserted back to base styles (clears any stranded inline pre-hide).
const _IOS_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)' // springBezier — entrances
const _EXIT_EASE = 'cubic-bezier(0.29, 0.67, 0.51, 0.97)' // defaultBezier — exits
const _SHEET_ENTER_MS = 355 // timing D
const _SHEET_EXIT_MS = 267 // timing C
// The backstops settle a run whose animation events never arrive (a document
// that renders no frames) — the run's own length plus the slack the old
// numbers carried.
const _SHEET_ENTER_BACKSTOP_MS = _SHEET_ENTER_MS + 250
const _SHEET_EXIT_BACKSTOP_MS = _SHEET_EXIT_MS + 150
const _PANE_ENTER = [
  { opacity: 0, transform: 'translate3d(0, 2.5%, 0) scale(0.97)' },
  { opacity: 1, transform: 'none' }
]
const _PANE_EXIT = [
  { opacity: 1, transform: 'none' },
  { opacity: 0, transform: 'translate3d(0, 2.5%, 0) scale(0.97)' }
]
// Test seam (__tests__/pageSlotSheetMotion.test.js) — the one recipe, read
// back as data so the token lock needs no DOM.
export const __sheetMotionForTests = {
  enter: { keyframes: _PANE_ENTER, duration: _SHEET_ENTER_MS, easing: _IOS_EASE },
  exit: { keyframes: _PANE_EXIT, duration: _SHEET_EXIT_MS, easing: _EXIT_EASE },
  backstop: { enter: _SHEET_ENTER_BACKSTOP_MS, exit: _SHEET_EXIT_BACKSTOP_MS }
}
// Asked at each use, never at module load (canvas case 2, 2026-11-01): frank
// evaluates module scope in its build sandbox and emits the value it got there
// as data, so a module-load MediaQueryList froze `matches: false` and the
// served canvas animated for a person who asked for reduced motion. A function
// is emitted as a function (the `_canvasIsPhone` shape below).
const _prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const _clearInline = (el) => {
  if (el.node && el.node.style) {
    el.node.style.opacity = ''
    // …and the inline translate the phone drag-to-dismiss gesture parks on the
    // pane. A run's settle IS the gesture's self-heal: a committed dismissal
    // must never leave the sheet translated for its next open.
    el.node.style.transform = ''
  }
}

// A pane animates only when someone can see it AND wants motion.
const _canAnimate = (el) =>
  !!(el.node && typeof el.node.animate === 'function') &&
  typeof document !== 'undefined' &&
  document.visibilityState !== 'hidden' &&
  !_prefersReducedMotion()

// ── Focus in, focus back (DESIGN_SPEC §6.6) ─────────────────────────────────
//
// Opening a window moves focus INTO it and closing it hands focus back to
// what held it before: the widget title, the launcher tile, the search
// result's return target. (The window is not a modal dialog — see the note on
// the frame — so Tab still reaches the composer floating over it.) The PANE takes the focus
// (`tabindex: -1`, labelled by its title), so a screen reader names the window
// first; a page that focuses its own field when it mounts (a composer, a
// search box) still wins, because it mounts after.
//
// It never steals: not while the header search is open (a split pick is typed
// there), not for the split pane's placeholder (no route yet — the search owns
// the keyboard), and not when focus already sits inside the pane. And it never
// hands focus back INTO a field: a field's own focus handler can open
// something (the header search's launcher does), and no sheet is opened from
// one.
const _isField = (n) => {
  if (!n || n.nodeType !== 1) return false
  const tag = String(n.tagName || '').toLowerCase()
  return !!(
    n.isContentEditable ||
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    (typeof n.getAttribute === 'function' &&
      ['textbox', 'combobox', 'searchbox'].includes(n.getAttribute('role')))
  )
}

// What held focus at a pane's open edge — the target focus returns to.
const _focusOrigin = (el) => {
  if (typeof document === 'undefined') return null
  const a = document.activeElement
  if (!a || a === document.body || _isField(a)) return null
  if (el.node && typeof el.node.contains === 'function' && el.node.contains(a)) return null
  return a
}

const _focusPaneIn = (el) => {
  const node = el.node
  if (!node || typeof node.focus !== 'function' || typeof document === 'undefined') return
  const rootEl = el.getRoot && el.getRoot()
  const root = rootEl && rootEl.state
  if (!root || !root[el.scope?.SLOT_KEY] || root._headerSearchOpen) return
  const active = document.activeElement
  if (active && active !== document.body && node.contains(active)) return
  try {
    node.focus({ preventScroll: true })
  } catch (_) {
    /* a detached node takes no focus — nothing to move */
  }
}

// Only when the keyboard is IN the closing pane (or nowhere): focus that has
// already moved on (the X of the surviving pane, the header) stays put.
const _returnPaneFocus = (el, root) => {
  const target = el._returnFocus
  el._returnFocus = null
  if (typeof document === 'undefined') return
  const node = el.node
  const active = document.activeElement
  const lost = !active || active === document.body
  if (!lost && !(node && typeof node.contains === 'function' && node.contains(active))) return
  const usable = (n) =>
    !!n &&
    n.isConnected !== false &&
    typeof n.focus === 'function' &&
    !_isField(n) &&
    !(node && node.contains(n))
  if (usable(target)) {
    try {
      target.focus({ preventScroll: true })
      return
    } catch (_) {
      /* fall through to the surviving pane */
    }
  }
  // The split pane closing (its X, Escape, a veil press) with the primary still
  // open: the keyboard goes to the window that stays.
  const frame = el.parent
  const other = frame && (el.scope?.primary ? frame.PaneSecond : frame.PanePrimary)
  const otherOpen = other && root && root[other.scope?.SLOT_KEY]
  if (otherOpen && other.node && typeof other.node.focus === 'function') {
    try {
      other.node.focus({ preventScroll: true })
    } catch (_) {
      /* noop */
    }
  }
}

// ── Phone sheet (≤ mobileL) ────────────────────────────────────────────────
//
// On a phone the slot stops being a floating card and becomes an iOS sheet: it
// owns the screen edge-to-edge, keeps a thin reveal of the scrimmed app above
// it so it still reads as a LAYER (not a new screen), and answers to the
// platform's phone gesture vocabulary — the same verbs the left-edge rail pull
// teaches (shared/functions/edgeSwipe.js): follow the finger with a
// transform-only write, commit past a fraction or a flick, otherwise settle
// back on the iOS curve. Geometry is CSS-gated at `@mobileL` (desktop/tablet
// bytes unchanged); the gesture is additionally gated coarse-pointer, so a
// touchscreen laptop above 768 never arms it either.
const _PHONE_MQ = '(max-width: 768px)'
// Sheet top inset = notch/status-bar inset + a ~10px reveal (spacing Z) of the
// page behind. Same env()+token form the shell's own --shell-top uses (app.js:
// `calc(var(--spacing-C1) + 3px + env(safe-area-inset-top))`), so the sheet
// clears hardware exactly the way every other chrome island does — and the
// reveal is a TOKEN, not a magic pixel count.
const _PHONE_TOP = 'calc(env(safe-area-inset-top) + var(--spacing-Z))'
const _canvasIsPhone = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(_PHONE_MQ).matches

// THE PHONE SHEET OWNS EVERY STATE. The four conditional blocks on PageSlot
// (.isSlotFullscreen, .isSlotRailEdge, .isSlotExpanded, .isSlotRailInset)
// paint INLINE, so the '@mobileL' rule that collapses each of them to the
// sheet never outranks them: measured on dev (Bellforge, 390px), the admin's
// expanded default kept `bottom: var(--slot-bottom)` inline and the sheet
// stopped 74px short of the screen edge. On a phone none of them applies.
// `_isPhoneViewport` is the shell's answer (its phone dock and rail sheets
// publish it); a host that never publishes it falls back to the query.
const _slotOnPhone = (root) =>
  root?._isPhoneViewport === true || (root?._isPhoneViewport == null && _canvasIsPhone())

// Commit thresholds. The fraction is the sheet's own height (25% per the phone
// sheet grammar); the flick velocity mirrors edgeSwipe's FLICK_PX_PER_MS so a
// quick throw feels the same everywhere. The axis slop matches it too — below
// it nothing moves, so a tap on a header button is never a drag.
const _DRAG_COMMIT_FRACTION = 0.25
const _DRAG_FLICK_PX_PER_MS = 0.35
const _DRAG_AXIS_SLOP = 8
// The grip regions: the grabber pill and the pane header. The sheet BODY is
// left alone — it scrolls.
const _GRIP_SELECTOR = '[data-sheet-grip]'

// Read the pane's live translateY, so an interrupted settle is picked up
// exactly where it was rather than snapping to 0. Read BEFORE cancelling the
// animation — computed style carries the animated value only while it runs.
const _currentTranslateY = (node) => {
  try {
    const t = window.getComputedStyle(node).transform
    if (!t || t === 'none') return 0
    return new window.DOMMatrixReadOnly(t).m42 || 0
  } catch (_) {
    return 0
  }
}

// Tear down a settle run (animation + backstop timer) WITHOUT touching the
// inline transform — the caller decides where the sheet should sit next.
// Handlers are detached before cancel, same frame-independence discipline the
// open/close runs use above.
const _cancelDragAnim = (el) => {
  if (el._dragT) {
    clearTimeout(el._dragT)
    el._dragT = null
  }
  const anim = el._dragAnim
  el._dragAnim = null
  if (anim) {
    anim.onfinish = null
    anim.oncancel = null
    try {
      if (anim.playState !== 'finished') anim.cancel()
    } catch (_) {
      /* noop */
    }
  }
}

// Released below the commit threshold → ride back to rest. Interruptible (a new
// grab cancels it and continues from the live offset), and — like every run in
// this file — never dependent on the animation event: a timer backstop clears
// the inline transform even in a document that stops producing frames, and a
// reduced-motion / hidden document lands it instantly.
const _dragSettleBack = (el) => {
  const node = el.node
  _cancelDragAnim(el)
  if (!node || !node.style) return
  const from = node.style.transform
  const done = () => {
    _cancelDragAnim(el)
    if (el.node && el.node.style) el.node.style.transform = ''
  }
  if (!from || !_canAnimate(el)) return done()
  let anim = null
  try {
    anim = node.animate([{ transform: from }, { transform: 'none' }], {
      duration: 260,
      easing: _IOS_EASE
    })
  } catch (_) {
    /* fall through to an instant snap back */
  }
  if (!anim) return done()
  el._dragAnim = anim
  anim.onfinish = done
  anim.oncancel = done
  el._dragT = setTimeout(done, 420)
}

// Drag-to-dismiss on a phone pane. The four pointer handlers are DECLARED on
// the pane (see `onPointerdown` / `onPointermove` / `onPointerup` /
// `onPointercancel` in `_slotPane`); this factory builds them once per pane
// element and caches them on `el.__sheetDrag`.
//
// They used to be four raw `node.addEventListener(..., { passive: true })`
// calls armed from `onRender` behind a `node.__sheetDragArmed` flag, removed
// nowhere (FA503). Nothing about the GESTURE changed — only who owns the
// registration. Two things got better on the way:
//   • the handlers now read `el.node` LIVE instead of closing over one node,
//     so a node re-creation (keep-alive slot re-mount) can no longer leave the
//     gesture pointing at a dead node;
//   • `pointercancel` is not in smbls's DELEGATED_EVENTS, so it becomes a real
//     node listener that dispose() removes — the one whose absence strands a
//     half-dragged sheet mid-screen.
//
// Listeners stay PASSIVE-safe: `touch-action: none` on the grip regions stops
// the browser claiming the vertical pan, so nothing here ever needs
// preventDefault.
//
// `undefined` = not decided yet; `null` = decided NO (a fine pointer), cached
// so a desktop pointermove never re-asks.
const _sheetDragHandlers = (el) => {
  if (el.__sheetDrag !== undefined) return el.__sheetDrag
  const node = el.node
  if (!node) return null // no node yet — decide on the next event, don't cache
  // Coarse pointers only — desktop/tablet-with-mouse keeps the header's Close
  // button as its single dismissal.
  if (typeof el.call === 'function' && !el.call('isCoarsePointer')) {
    el.__sheetDrag = null
    return null
  }
  const primary = el.scope?.primary
  const slotKey = el.scope?.SLOT_KEY

  // Transient gesture state — closure-local, never reactive (the edgeSwipe /
  // longPress idiom: a mid-gesture y coordinate has no business in signals).
  let tracking = false
  let captured = false
  let base = 0
  let startX = 0
  let startY = 0
  let startTs = 0
  let lastY = 0
  let lastTs = 0
  let axis = null

  const rootState = () =>
    typeof el.getRootState === 'function' ? el.getRootState() : el.state && el.state.root

  const paint = (y) => {
    if (!el.node || !el.node.style) return
    el.node.style.transform = y > 0 ? `translateY(${Math.round(y)}px)` : ''
  }

  const stop = (e) => {
    tracking = false
    el._dragging = false
    if (!captured) return
    captured = false
    try {
      el.node.releasePointerCapture(e.pointerId)
    } catch (_) {
      /* noop */
    }
  }

  const onDown = (e) => {
    if (e.pointerType === 'mouse' || !_canvasIsPhone()) return
    const t = e.target
    if (!t || typeof t.closest !== 'function' || !t.closest(_GRIP_SELECTOR)) return
    // Never fight the open/close choreography — a run owns the transform while
    // it is in flight.
    if (el._anim || el._enterQueued || el._closing) return
    // Interruptible: a new grab picks the sheet up wherever the settle left it.
    base = el._dragAnim ? Math.max(0, _currentTranslateY(el.node)) : 0
    _cancelDragAnim(el)
    tracking = true
    el._dragging = true
    captured = false
    axis = null
    startX = e.clientX
    startY = lastY = e.clientY
    startTs = lastTs = e.timeStamp
  }

  const onMove = (e) => {
    if (!tracking) return
    const dy = e.clientY - startY
    const dx = e.clientX - startX
    if (!axis) {
      if (Math.abs(dy) < _DRAG_AXIS_SLOP && Math.abs(dx) < _DRAG_AXIS_SLOP) return
      // A horizontal intent on the header is not a dismissal — drop the
      // gesture rather than fighting whatever owns that axis.
      axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'veto'
      if (axis === 'y') {
        // Capture only once it IS a drag: capturing on pointerdown would
        // swallow taps on the header's own buttons.
        captured = true
        try {
          el.node.setPointerCapture(e.pointerId)
        } catch (_) {
          captured = false
        }
      }
    }
    if (axis !== 'y') {
      stop(e)
      return
    }
    lastY = e.clientY
    lastTs = e.timeStamp
    // Downward only; an upward pull holds the sheet at rest (no rubber band —
    // there is nothing above it to reveal).
    paint(Math.max(0, base + dy))
  }

  const onUp = (e) => {
    if (!tracking) return
    const wasAxis = axis
    if (typeof e.clientY === 'number') lastY = e.clientY
    lastTs = e.timeStamp
    stop(e)
    if (wasAxis !== 'y') return
    const travel = Math.max(0, base + (lastY - startY))
    const height = el.node.getBoundingClientRect().height || 1
    const dt = Math.max(1, lastTs - startTs)
    const flick = (lastY - startY) / dt
    if (travel < height * _DRAG_COMMIT_FRACTION && flick < _DRAG_FLICK_PX_PER_MS) {
      _dragSettleBack(el)
      return
    }
    // Committed. Hand the finger's offset to the EXISTING exit choreography so
    // the sheet keeps moving from where it was let go, then close through the
    // ONE dismissal path (_dismissPane — the header CloseBtn's own).
    _cancelDragAnim(el)
    el._dragFrom = travel
    const root = rootState()
    _dismissPane(root, primary)
    // …except when that path PROMOTES instead of closing: dismissing a primary
    // that has a split pane open hands it the surviving route, so this pane
    // stays mounted and never runs an exit. Ride the offset back to rest rather
    // than stranding the sheet mid-screen.
    if (root && root[slotKey]) {
      el._dragFrom = 0
      _dragSettleBack(el)
    }
  }

  el.__sheetDrag = { onDown, onMove, onUp }
  return el.__sheetDrag
}

// Begin a choreography run: invalidate the previous run (its settle becomes a
// no-op via the token), detach its handlers BEFORE cancelling (so no event can
// fire for it even when frames resume later), and clear its backstop timer.
const _startRun = (el) => {
  const token = (el._runSeq = (el._runSeq || 0) + 1)
  if (el._runT) {
    clearTimeout(el._runT)
    el._runT = null
  }
  const prev = el._anim
  el._anim = null
  if (prev) {
    prev.onfinish = null
    prev.oncancel = null
    try {
      prev.cancel()
    } catch (_) {
      /* noop */
    }
  }
  return token
}

// The ONE idempotent settle for a run: tears down the animation, restores base
// styles, then runs the transition's tail work. Safe from any caller — event,
// backstop timer, or synchronous skip — and inert once superseded.
const _finishRun = (el, token, tail) => () => {
  if (el._runSeq !== token) return
  if (el._runT) {
    clearTimeout(el._runT)
    el._runT = null
  }
  const anim = el._anim
  el._anim = null
  if (anim) {
    anim.onfinish = null
    anim.oncancel = null
    try {
      if (anim.playState !== 'finished') anim.cancel()
    } catch (_) {
      /* noop */
    }
  }
  _clearInline(el)
  if (tail) tail()
}

const _paneEnter = (el) => {
  const token = _startRun(el)
  el._closing = false
  const settle = _finishRun(el, token)
  // Focus moves IN (DESIGN_SPEC §6.6) before the entrance — the sheet is
  // modal, so the keyboard follows the eye. Independent of the animation: a
  // document that renders no frames (or prefers reduced motion) lands the
  // final state at once and still gets the focus.
  _focusPaneIn(el)
  if (!_canAnimate(el)) return settle()
  let anim = null
  try {
    // The inline opacity 0 was already applied by the hide factory
    // (pre-paint); the enter keyframes take over from that same value.
    anim = el.node.animate(_PANE_ENTER, { duration: _SHEET_ENTER_MS, easing: _IOS_EASE })
  } catch (_) {
    /* fall through to instant show */
  }
  if (!anim) return settle()
  el._anim = anim
  anim.onfinish = settle
  anim.oncancel = settle
  el._runT = setTimeout(settle, _SHEET_ENTER_BACKSTOP_MS)
}

// A drag-committed dismissal continues from the finger's last offset and throws
// the sheet off the bottom edge, instead of snapping back to 0 and fading in
// place (what _PANE_EXIT does for a click-close). Same duration/easing, so the
// close choreography, its backstop and its tail work are untouched — only the
// keyframes differ, and only when a drag set _dragFrom.
const _paneThrow = (from) => [
  { opacity: 1, transform: `translateY(${Math.round(from)}px)` },
  { opacity: 0, transform: 'translateY(100%)' }
]

const _paneExit = (el, root) => {
  const token = _startRun(el)
  el._closing = true
  // Consume the gesture's offset exactly once — a later click-close must get
  // the plain exit back.
  const dragFrom = el._dragFrom
  el._dragFrom = 0
  const settle = _finishRun(el, token, () => {
    el._closing = false
    // Re-run the hide effect via a STATE nudge — `el.update({})` does not
    // re-evaluate reactive props, and effects only re-run on signals READ in
    // the factory (see the two OPEN tickets in tickets/smbls.md), so the
    // factory reads _slotNudge.
    try {
      root && root.update && root.update({ _slotNudge: (root._slotNudge || 0) + 1 })
    } catch (_) {
      /* torn down mid-close */
    }
  })
  // Focus goes back where it was before the pane opened (DESIGN_SPEC §6.6),
  // at the START of the close — the pane is on its way out, and a keyboard
  // user must not sit inside a fading sheet.
  _returnPaneFocus(el, root)
  if (!_canAnimate(el)) return settle()
  let anim = null
  try {
    anim = el.node.animate(dragFrom ? _paneThrow(dragFrom) : _PANE_EXIT, {
      duration: _SHEET_EXIT_MS,
      easing: _EXIT_EASE
    })
  } catch (_) {
    /* fall through to instant hide */
  }
  if (!anim) return settle()
  el._anim = anim
  anim.onfinish = settle
  anim.oncancel = settle
  el._runT = setTimeout(settle, _SHEET_EXIT_BACKSTOP_MS)
}

// Schedule the entrance from the hide factory itself. A microtask runs after
// the whole update pass (display applied, node attached) but BEFORE the next
// paint — so the inline pre-hide still can't flash, and the enter no longer
// depends on onCreate/onUpdate ordering. (Verified live 2026-07-12: on repeat
// opens the hide effect can run AFTER the element's own update hooks — or as
// an isolated signal effect with no hooks at all — so a "pending" flag flushed
// from onUpdate strands the pane at the inline opacity 0.) The _paneShown
// check makes a same-tick close cancel the queued entrance.
const _queueEnter = (el) => {
  if (el._enterQueued) return
  el._enterQueued = true
  queueMicrotask(() => {
    el._enterQueued = false
    if (el._paneShown) _paneEnter(el)
  })
}

// Mount the pane's page def into its per-route KEEP-ALIVE HOST (see the
// minimize-to-dock section above). A route whose host already exists is a
// keep-alive hit: NO re-mount — the host's display predicate reveals the
// SAME element instance, which is the whole minimize/restore contract. A
// same-route open with a DIFFERENT query re-mounts fresh (`_slotPath`),
// matching the old single-mount semantics for deep links. The heavy page
// prep stays DEFERRED past the next paint: smbls element preparation is
// synchronous and super-linear in page size, so setting inline at click
// blocked the frame — the sheet appeared late and "blinked" (2026-07-12 per
// Nika "opening is slow and blinks"). `_lastSlot` doubles as the in-flight
// dedupe guard; the seq guards a rapid route switch from a stale late set;
// the timer is the fallback for throttled tabs where rAF crawls.
// Release the in-flight/dedupe guard when a DEFERRED apply() bails without
// producing a host.
//
// `_lastSlot` is written the moment a mount is REQUESTED, but the host is
// only created a frame later inside apply(). Every early return in there —
// no `context.__create`, a `__create` that produced no `__ref`, a page def
// that throws inside `set()` — used to leave `_lastSlot` naming a host that
// does not exist. From then on `_mountPane`'s own `if (el._lastSlot === path)
// return` refused every later attempt at that route, so the pane stayed
// EMPTY FOREVER — it rendered its apps rail and nothing else, with no
// `_slotPath` host under it, and none of the retry subscriptions
// (`workspaceProject`, `_minimizedSlots`, `_slotNudge`) could heal it because
// they all funnel through that same guard. Exactly the shape of the
// second-pane mount gap the D2 split-pane pass reported.
//
// Only the apply that still OWNS the mount-seq may release it: a newer
// request has already re-pointed `_lastSlot` at its own route and must not
// have its guard cleared by a superseded predecessor.
const _releaseMountGuard = (el, path, seq) => {
  if (seq !== el._mountSeq) return
  if (el._lastSlot === path) el._lastSlot = null
}

// Run `fn` AFTER the next paint, never before it. A rAF callback runs BEFORE
// its own frame's paint, so the old `requestAnimationFrame(apply)` prepared
// the page inside the very frame that should have SHOWN the window — the
// window (and its entrance) waited for the whole page. A rAF queued from a
// rAF runs in the FOLLOWING frame, after the first one painted the window and
// its skeleton. The plain timer is the backstop for a document that renders
// no frames (a hidden tab, where rAF never fires) — the 120 ms the mount
// always carried. `apply` itself is idempotent and seq-guarded, so whichever
// fires first wins and the other is a no-op.
const _afterPaint = (fn, setFallback) => {
  if (typeof requestAnimationFrame !== 'function') return fn()
  requestAnimationFrame(() => requestAnimationFrame(fn))
  setFallback(setTimeout(fn, 120))
}

// The skeleton first (see `PageSkeleton`): create the route's keep-alive host
// NOW — cheap, one small subtree — with the skeleton as its content. The host
// is stamped for the sweep (its route, workspace and pane, so it is the
// ACTIVE host and survives) but NOT with `_slotPath`: that stamp means "the
// page is mounted", which is what the keep-alive fast path reveals. Instead
// `_slotPending` names the route whose page is on its way. Best-effort: with
// no skeleton registered (a context without the canvas slice) or no create
// path, the window opens exactly as before and the page lands on its own.
const _showSkeleton = (el, s, SLOT_KEY, clean, path) => {
  if (!el.context?.components?.PageSkeleton) return
  const createFn = el.context?.__create
  if (!createFn) return
  const hostKey = _hostKeyFor(clean)
  try {
    if (!el[hostKey] || !el[hostKey].__ref) createFn(_hostDef(clean, SLOT_KEY), el, hostKey)
    const host = el[hostKey]
    if (!host || !host.__ref || typeof host.set !== 'function') return
    host._slotHref = clean
    host._slotWs = _canvasWsIdOf(s.root)
    host._slotPaneKey = SLOT_KEY
    host._slotPending = path
    host.set({ extends: 'PageSkeleton' })
  } catch (_) {
    /* the page's own mount still lands */
  }
}

// A family host's first mount gets the same skeleton, in its Body (the swap
// target); its Chrome stays empty until the apply mounts it once.
const _showFamilySkeleton = (el, s, SLOT_KEY, clean, family, hostKey) => {
  if (!el.context?.components?.PageSkeleton) return
  const createFn = el.context?.__create
  if (!createFn || (el[hostKey] && el[hostKey].__ref)) return
  try {
    createFn(_familyHostDef(family, SLOT_KEY), el, hostKey)
    const host = el[hostKey]
    if (!host || !host.__ref || !host.Body || typeof host.Body.set !== 'function') return
    host._slotHref = clean
    host._slotFamily = family.id
    host._slotWs = _canvasWsIdOf(s.root)
    host._slotPaneKey = SLOT_KEY
    host.Body.set({ extends: 'PageSkeleton' })
  } catch (_) {
    /* the page's own mount still lands */
  }
}

const _mountPane = (el, s, SLOT_KEY) => {
  const path = s.root?.[SLOT_KEY]
  if (!path) return
  const clean = canvasCleanHref(path)
  // Family routes take the shared-host path: Chrome persists, Body swaps.
  const family = _familyOf(s.root, clean)
  if (family && family.id) {
    _mountFamilyPane(el, s, SLOT_KEY, path, clean, family)
    return
  }
  const hostKey = _hostKeyFor(clean)
  const live = el[hostKey]
  if (live && live.__ref) {
    if (live._slotPath === path) {
      // Keep-alive hit — reveal only. Nothing here (or anywhere) re-creates
      // the element, so scroll, inputs, timers and in-flight work persist.
      // LRU stamp: a reveal counts as use (module keep-alive eviction order).
      live._slotSeenAt = Date.now()
      el._lastSlot = path
      return
    }
    // The skeleton is up for THIS route and its page is on the way (the
    // deferred apply below owns it): a re-render in between waits for it
    // instead of tearing the skeleton down and starting over.
    if (live._slotPending === path && el._lastSlot === path) return
    // Same route, different query — a deep-link change re-mounts, exactly
    // like the pre-host `el.set` replacement did.
    if (canvasCleanHref(el._lastSlot) === clean) el._lastSlot = null
    live.remove()
  }
  if (el._lastSlot === path) return
  const def = _defFor(el, path, s.root)
  if (!def) return
  el._lastSlot = path
  const seq = (el._mountSeq = (el._mountSeq || 0) + 1)
  // THE SHELL FIRST: the host exists now, with the skeleton as its content,
  // so the window paints a page-shaped skeleton in the very next frame.
  _showSkeleton(el, s, SLOT_KEY, clean, path)
  let applied = false
  let fallbackT = null
  const apply = () => {
    if (applied || seq !== el._mountSeq) return
    applied = true
    if (fallbackT) clearTimeout(fallbackT)
    try {
      // Create the keep-alive host through the same context `__create` path
      // `el.set()` uses internally (methods.js), then mount the page INTO it
      // — `set` keeps its contentManaged semantics per host.
      const createFn = el.context?.__create
      if (!createFn) return _releaseMountGuard(el, path, seq)
      if (!el[hostKey] || !el[hostKey].__ref) createFn(_hostDef(clean, SLOT_KEY), el, hostKey)
      const host = el[hostKey]
      if (!host || !host.__ref) return _releaseMountGuard(el, path, seq)
      // Workspace stamp + full-path identity — consumed by the sweep and the
      // query-change re-mount above.
      host._slotHref = clean
      host._slotPath = path
      host._slotPending = null
      host._slotWs = _canvasWsIdOf(s.root)
      // LRU stamp for the module keep-alive plan (_moduleKeepPlan).
      host._slotSeenAt = Date.now()
      // Pane identity for the split-view pane-scoping contract: openers fired
      // from INSIDE a mounted page resolve their origin pane by walking to
      // this stamp (shared/functions/slotPane.js slotPaneKeyFor), so an
      // in-pane navigation targets its OWN pane's slot key — never the
      // sibling's.
      host._slotPaneKey = SLOT_KEY
      // Strip page-grid placement (gridRow/gridColumn target the shell's
      // content row, not a slot) and make the def fill the host — unless it
      // is a content-sized page (an answer view, slotPageFill.js).
      host.set({
        ...def,
        // `context` explicitly: workspace-module defs carry a NON-ENUMERABLE
        // design-system context (workspaceProjects.js _wrapModulePages) and
        // spread copies only enumerable props — without this line the module
        // page mounts against the SHELL config and every module token
        // resolves wrong ("navy" → CSS keyword, "paper" drops).
        context: def.context,
        tag: 'div',
        // A pane is a labelled region (the frame's role="region"), never a
        // second main: the route-level stamps a def carries (workspace
        // context.js anchorMainContent — role main + #main-content) step back
        // here, and a lazy page's placeholder drops its page's own the same
        // way (pages/lazyPage.js lazyPageUnanchor reads this host's
        // `_slotPaneKey`). WS-SHELL-APP-ROUTES-NO-MAIN-LANDMARK-1.
        ...(def.role === 'main' ? { role: null } : {}),
        ...(def.id === 'main-content' ? { id: null } : {}),
        ...(def.attr && def.attr.id === 'main-content' ? { attr: { ...def.attr, id: null } } : {}),
        gridRow: null,
        gridColumn: null,
        ...slotPageFillProps(def)
      })
      // The mounted page's helmet just wrote ITS title — hand it back to the
      // primary (a second-pane mount must not keep the tab title).
      _syncSlotTitle(el, s.root)
    } catch (err) {
      console.warn('[PageSlot] mount failed:', err)
      // A HALF-MOUNTED host is worse than no host at all. The stamps land
      // BEFORE `set()` on purpose (the page's own onCreate/onRender run
      // inside `set` and read `_slotPath` — that is the "read the deep-link
      // param AT MOUNT" contract), so a def that throws in `set` leaves a
      // host whose stamps already claim the route with nothing inside it.
      // The keep-alive fast path at the top of `_mountPane` would then match
      // `live._slotPath === path` and REVEAL that empty host on every later
      // open of the route, forever. Drop it, then release the guard.
      try {
        const halfMounted = el[hostKey]
        if (halfMounted && halfMounted.__ref) halfMounted.remove()
      } catch (_) {
        /* already detached */
      }
      _releaseMountGuard(el, path, seq)
    }
  }
  _afterPaint(apply, (t) => {
    fallbackT = t
  })
}

// Family-host mount — _mountPane's sibling for family routes. Mirrors its
// dedupe guard, mount-seq race guard and rAF deferral; differs in the swap:
// a same-family route change re-`set`s ONLY `host.Body`, leaving the
// Chrome subtree (and its DOM nodes) untouched.
//
// "Nothing to do" is decided from `el._lastSlot` ALONE — updated
// synchronously below, on every request. The live host's OWN `_slotPath`
// stamp is NOT a safe substitute for that check: it only updates once a
// DEFERRED apply() actually runs (rAF, or the setTimeout fallback), so on
// a family host — where two DIFFERENT routes share one `hostKey` — a
// second request arriving before the first one's apply() has fired would
// read that STALE stamp as "already showing this" and return early
// WITHOUT invalidating the in-flight mount-seq. Reproduced live
// (packages/canvas/__tests__/pageSlotFamilyHost.test.js "a rapid second
// navigation…"): settle on route A, click to sibling route B (left
// in-flight, not yet applied), then click BACK to A before B's apply()
// runs — B's request matches the host's not-yet-updated `_slotPath` from
// before A ever applied, so the click-back-to-A request returned early,
// A's mount-seq bump never happened, and B's stale apply() won the race
// and landed in Body instead of A.
const _mountFamilyPane = (el, s, SLOT_KEY, path, clean, family) => {
  if (el._lastSlot === path) return
  const hostKey = _familyHostKey(family.id)
  const def = _defFor(el, path, s.root)
  if (!def) return
  el._lastSlot = path
  const seq = (el._mountSeq = (el._mountSeq || 0) + 1)
  // The skeleton first, as for a per-route host — but only for a family's
  // FIRST mount: a sibling route swaps an already-painted Body in place.
  _showFamilySkeleton(el, s, SLOT_KEY, clean, family, hostKey)
  let applied = false
  let fallbackT = null
  const apply = () => {
    if (applied || seq !== el._mountSeq) return
    applied = true
    if (fallbackT) clearTimeout(fallbackT)
    try {
      const createFn = el.context?.__create
      if (!createFn) return _releaseMountGuard(el, path, seq)
      if (!el[hostKey] || !el[hostKey].__ref)
        createFn(_familyHostDef(family, SLOT_KEY), el, hostKey)
      const host = el[hostKey]
      if (!host || !host.__ref) return _releaseMountGuard(el, path, seq)
      host._slotHref = clean
      host._slotPath = path
      host._slotFamily = family.id
      host._slotWs = _canvasWsIdOf(s.root)
      host._slotPaneKey = SLOT_KEY
      // Chrome mounts ONCE per host — the family contract: intra-family
      // route swaps must not touch these DOM nodes. The chrome def resolves
      // through the SAME `_defFor` channel as pages (`chromePath` — a
      // pseudo-page the family package registers), so it stays in the
      // family's lazy chunk and folds its context exactly like a page.
      // "Once" is the host's own stamp, not "the host is new": the skeleton
      // may have created the host a frame before this apply.
      const chromeDef = family.chromePath
        ? _defFor(el, family.chromePath, s.root)
        : family.chrome || null
      if (!host._slotChromeSet && chromeDef && host.Chrome) {
        host.Chrome.set({
          ...chromeDef,
          context: chromeDef.context || def.context,
          tag: 'div'
        })
        host._slotChromeSet = true
      }
      if (host.Body) {
        host.Body.set({
          ...def,
          context: def.context,
          tag: 'div',
          gridRow: null,
          gridColumn: null,
          height: '100%',
          minHeight: '100%'
        })
      }
      _syncSlotTitle(el, s.root)
    } catch (err) {
      console.warn('[PageSlot] family mount failed:', err)
      // Same recovery as the per-route host above: a family host whose
      // Chrome or Body threw is stamped for a route it never rendered, and
      // the fast path would keep revealing it. Drop the whole host — a
      // retry re-mounts Chrome and Body together.
      try {
        const halfMounted = el[hostKey]
        if (halfMounted && halfMounted.__ref) halfMounted.remove()
      } catch (_) {
        /* already detached */
      }
      _releaseMountGuard(el, path, seq)
    }
  }
  _afterPaint(apply, (t) => {
    fallbackT = t
  })
}

// ── Header left cluster + apps rail (2026-08-06 per Nika "when we navigate
// accross pageslots - we shuld also support back and forward navigations in
// pageslots. add it on the left side before pageslot title. also support
// sidebar view in pageslot thats quick access for all available pageslot
// apps. so left arrpw, right arrow, sidebar toggle that opens new pageslot
// sidebar that has navigation floating above it for pageslot apps — make it
// smooth and beautiful") ───────────────────────────────────────────────────

// One recipe for both arrows — the right cluster's PageSlotHeaderBtn
// geometry, quieted to a dimmed no-op when its direction has no history
// (kept in the layout so the cluster never reflows). Disabled = opacity +
// pointerEvents + aria-disabled; the native `disabled` attr is deliberately
// avoided (it drops the button from the tab order and mutes the tooltip).
// STRICTLY PANE-SCOPED: reads/writes THIS pane's history + slot key only.
//
// FRANK-SAFE BY CONSTRUCTION (2026-08-12, tickets/opus.md "~800 project-side
// Frankability defects" — canvas's PageSlot factory-closure-capture class):
// `HIST_KEY`/`SLOT_KEY`/`back` used to be plain JS closures over THIS
// factory's own params/locals. A closure over a factory param/local doesn't
// survive frank.toJSON — it lifts every reactive prop into a standalone
// function, and a free variable that is neither that function's own
// parameter nor a module-scope const resolves to nothing at eval-revival
// time (a guaranteed ReferenceError/undefined the moment PageSlot is served
// through frank's runtime — verified via `smbls frank to-json .`, 62
// unresolvable-scope-read + 2 unresolvable-identifier findings rooted in
// this file, all cleared by this rewrite). Fixed the FA204 way: values that
// only vary per PANE (SLOT_KEY/RAIL_KEY/HIST_KEY/primary/placeholderKey) live
// in the pane's own `scope:` (declared once in `_slotPane`, inherited by
// every descendant that doesn't redeclare it — see element/src/create.js's
// scope-chain comment); values that vary per NODE within one pane (`back` —
// BackBtn vs ForwardBtn share a pane but need different directions) get
// their OWN `scope:` override on top. `_histCan` reads only its own `(el,
// s)` params + a real top-level import — no closure, so it's liftable to
// `globalScope` (or emitted inline) with no free-variable defect, unlike the
// old shared `can` local (which frank tried to lift to `globalScope.can` and
// then flagged `back`/`HIST_KEY` as unresolvable on it).
const _histCan = (el, s) =>
  el.scope.back
    ? histCanBack(s.root?.[el.scope.HIST_KEY])
    : histCanForward(s.root?.[el.scope.HIST_KEY])
const _histBtn = (SLOT_KEY, dir) => {
  const back = dir === 'back'
  return {
    extends: 'PageSlotHeaderBtn',
    subscribeTo: [SLOT_KEY, _HIST_KEYS[SLOT_KEY]],
    // Per-node override on top of the pane's shared scope — `back` differs
    // between this factory's two call sites (BackBtn vs ForwardBtn) even
    // though they share one pane's SLOT_KEY/HIST_KEY.
    scope: { back },
    // Absent (not disabled) where the shell doesn't declare slot history —
    // canvas standalone mounts this pane recipe too.
    hide: (el, s) => s.root?.[el.scope.HIST_KEY] === undefined,
    'aria-label': (el) =>
      el.call('polyglot', el.scope.back ? 'canvas.pageSlot.back' : 'canvas.pageSlot.forward'),
    title: (el) =>
      el.call('polyglot', el.scope.back ? 'canvas.pageSlot.back' : 'canvas.pageSlot.forward'),
    'aria-disabled': (el, s) => (_histCan(el, s) ? 'false' : 'true'),
    disabled: (el, s) => !_histCan(el, s),
    theme: (el, s) => (_histCan(el, s) ? 'transparent' : 'field-disabled'),
    cursor: (el, s) => (_histCan(el, s) ? 'pointer' : 'not-allowed'),
    Icon: { name: back ? 'chevronLeft' : 'chevronRight' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = s.root
      if (!root) return
      const step = el.scope.back
        ? histBack(root[el.scope.HIST_KEY])
        : histForward(root[el.scope.HIST_KEY])
      if (!step) return
      // ONE atomic write: cursor + route move together, and the recorder
      // recognises the landing by cursor equality — no re-push, no flag.
      root.update({ [el.scope.HIST_KEY]: step.hist, [el.scope.SLOT_KEY]: step.path })
    }
  }
}

// ── The window's SIDEBAR toggle (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1 —
// Nika 2026-09-28: "one consistent sidebar for every page that shows a left
// nav inside a slot … plus a hide/show toggle (the slot header sidebar icon),
// remembered per user") ──────────────────────────────────────────────────────
//
// ONE icon, ONE concept — the window's sidebar:
//   • a page that shows its own left nav (every such nav is the house
//     AppSidebar, which marks itself `data-app-sidebar` — the account
//     settings rail is the reference) → the icon hides and shows THAT nav.
//     The choice is the PERSON's and the PAGE's (Nika 2026-10-29: "Hide
//     fully, remember per page" — shared functions/pageSidebar.js): one key
//     per page, kept in their look (`homeLook.sidebarHidden`), the same one
//     the sidebar's own button and its reopen tab write, so the window and
//     the page at its own URL always agree. The session answers first, so a
//     press lands at once even before the row has loaded;
//   • a page with no nav of its own → the icon opens the apps rail below (the
//     in-window app switcher, Nika 2026-08-06), exactly as before.
// Motion (DESIGN_SPEC §6.4): the nav leaves with opacity + a 4 % slide
// towards its own edge on `C` defaultBezier and returns on `D` springBezier —
// opacity and transform only; the page reflows once, when the nav folds down
// to its reopen tab. Reduced motion and a hidden document land the final
// state at once.

// The active page's own navs in this pane: AppSidebar instances (the
// attribute is AppSidebar's own, `attr: { 'data-app-sidebar': '' }`) inside
// the keep-alive host that shows the pane's route. A DOMQL tree walk, never a
// DOM query; parked hosts (minimized pages) are left alone.
const _pageSidebarsOf = (el, root) => {
  const pane = _paneOf(el)
  const slot = pane && pane.Body && pane.Body.Slot
  const ref = slot && slot.__ref
  if (!ref || !ref.__children) return []
  const active = canvasCleanHref(root && root[el.scope.SLOT_KEY])
  const out = []
  for (const key of ref.__children) {
    if (typeof key !== 'string' || !key.startsWith(_HOST_PREFIX)) continue
    const host = slot[key]
    if (!host || host._slotHref !== active || typeof host.lookdownAll !== 'function') continue
    host.lookdownAll(
      (c) => !!(c && c.attr && Object.prototype.hasOwnProperty.call(c.attr, 'data-app-sidebar')),
      out
    )
  }
  return out
}

// THE PAGE'S OWN CHOICE (shared functions/pageSidebar.js, Nika 2026-10-29:
// "Hide fully, remember per page"): the window's page and the same page at
// its own URL share one key, so this icon and the sidebar's own button (and
// its reopen tab) always agree. `el` is inside the pane, so the read keys on
// the pane's route. `nav` — the page's own sidebar, when the caller has it —
// answers the SIDE: a page that has not opted into the side rule
// (`pageSidebar: true`) keeps its sidebar on the left, and so do the glyph
// and the slide. A bundle without the reader (an embed) has no choice to
// show: the sidebar stays.
const _sidebarPosture = (el, root, nav) => {
  if (!root || typeof el?.context?.functions?.pageSidebarRead !== 'function') {
    return { hidden: false, side: 'left', toggleable: false, key: null }
  }
  return el.call('pageSidebarRead', root, nav || el) || { hidden: false, side: 'left' }
}
const _sidebarHidden = (el, root) => !!_sidebarPosture(el, root).hidden

// Write the person's choice for the pane's page — through the look's one
// writer (pageSidebarSet → homeLookSet: this session at once, the record per
// page). Never a copy of the preferences row in the state.
const _writeSidebarHidden = (el, root, hidden) => {
  const now = _sidebarPosture(el, root)
  if (!now.toggleable || !now.key) return
  el.call('pageSidebarSet', now.key, hidden)
}

// The slide goes towards the sidebar's own edge: left for a left sidebar,
// right for one mirrored beside a left navbar (functions/navbarLayout.js).
const _sidebarOut = (side) => [
  { opacity: 1, transform: 'none' },
  { opacity: 0, transform: side === 'right' ? 'translate3d(4%, 0, 0)' : 'translate3d(-4%, 0, 0)' }
]
const _sidebarIn = (side) => [
  { opacity: 0, transform: side === 'right' ? 'translate3d(4%, 0, 0)' : 'translate3d(-4%, 0, 0)' },
  { opacity: 1, transform: 'none' }
]
const _SIDEBAR_OUT = _sidebarOut('left')
const _SIDEBAR_IN = _sidebarIn('left')
// Test seam — the recipe as data (__tests__/pageSlotSidebarToggle.test.js).
export const __sidebarMotionForTests = {
  out: { keyframes: _SIDEBAR_OUT, duration: _SHEET_EXIT_MS, easing: _EXIT_EASE },
  in: { keyframes: _SIDEBAR_IN, duration: _SHEET_ENTER_MS, easing: _IOS_EASE },
  outRight: { keyframes: _sidebarOut('right') },
  inRight: { keyframes: _sidebarIn('right') }
}

// Hide: the navs leave first, THEN the choice lands (the attribute takes them
// out of the flow — a display flip mid-animation would cut it). Show: the
// choice lands first (they re-enter the flow), then they arrive. Every run
// settles by its own backstop timer, never by an animation event alone.
const _toggleSidebar = (el, root) => {
  const navs = _pageSidebarsOf(el, root)
  const posture = _sidebarPosture(el, root, navs[0])
  if (!posture.toggleable) return
  const hide = !posture.hidden
  const side = posture.side
  const nodes = navs.map((c) => c.node).filter((n) => n && typeof n.animate === 'function')
  const motion =
    nodes.length > 0 &&
    typeof document !== 'undefined' &&
    document.visibilityState !== 'hidden' &&
    !_prefersReducedMotion()
  if (!hide) {
    _writeSidebarHidden(el, root, false)
    if (motion) {
      for (const n of nodes) {
        try {
          n.animate(_sidebarIn(side), { duration: _SHEET_ENTER_MS, easing: _IOS_EASE })
        } catch (_) {
          /* shown already — the motion is decoration */
        }
      }
    }
    return
  }
  if (!motion) {
    _writeSidebarHidden(el, root, true)
    return
  }
  let done = false
  const held = []
  // The hidden sidebar keeps its reopen tab on screen (shared AppSidebar), so
  // the held last frame (opacity 0) is released once the choice has landed —
  // the cards are gone by then and the tab shows at full strength.
  const land = () => {
    if (done) return
    done = true
    _writeSidebarHidden(el, root, true)
    const release = () => {
      for (const a of held) {
        try {
          a.cancel()
        } catch (_) {
          /* already gone */
        }
      }
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(release)
    else release()
  }
  for (const n of nodes) {
    try {
      const a = n.animate(_sidebarOut(side), {
        duration: _SHEET_EXIT_MS,
        easing: _EXIT_EASE,
        fill: 'forwards'
      })
      held.push(a)
      a.onfinish = land
    } catch (_) {
      /* the backstop lands it */
    }
  }
  setTimeout(land, _SHEET_EXIT_BACKSTOP_MS)
}

// Which sidebar this icon speaks for right now — read at every factory run
// (the pane's route, the person's choice and the rail each re-run it).
const _sidebarMode = (el, s) => (_pageSidebarsOf(el, s.root).length ? 'page' : 'rail')

const _railToggleBtn = (SLOT_KEY) => {
  // RAIL_KEY read via `el.scope.RAIL_KEY` below (pane-level scope, see
  // _slotPane) instead of closing over this local — see _histBtn's note.
  return {
    extends: 'PageSlotHeaderBtn',
    subscribeTo: [
      _RAIL_KEYS[SLOT_KEY],
      SLOT_KEY,
      'userPrefs',
      '_homeLook',
      '_isPhoneViewport',
      '_slotNudge'
    ],
    // Needs the launcher's app-group source (workspace shell registers
    // searchAppGroupsFor; canvas standalone doesn't) AND the rail key
    // declared — hidden where either is absent, the _slotEntryMeta
    // availability idiom.
    hide: (el, s) => {
      // Immersive module members never see the apps switcher — the module IS
      // their app (2026-08-14 per Nika, same directive as SplitBtn). First +
      // unconditional so the persona flip stays a tracked dep.
      const imm = el.context?.functions?.moduleImmersiveActive
      if (typeof imm === 'function' && imm(s.root)) return true
      return (
        s.root?.[el.scope.RAIL_KEY] === undefined ||
        typeof (
          el.context?.functions?.searchAppGroupsFor || el.context?.methods?.searchAppGroupsFor
        ) !== 'function'
      )
    },
    'aria-label': (el, s) =>
      el.call(
        'polyglot',
        _sidebarMode(el, s) === 'page'
          ? _sidebarHidden(el, s.root)
            ? 'canvas.pageSlot.sidebarShow'
            : 'canvas.pageSlot.sidebarHide'
          : 'canvas.pageSlot.appsSidebarAria'
      ),
    title: (el, s) =>
      el.call(
        'polyglot',
        _sidebarMode(el, s) === 'page'
          ? _sidebarHidden(el, s.root)
            ? 'canvas.pageSlot.sidebarShow'
            : 'canvas.pageSlot.sidebarHide'
          : 'canvas.pageSlot.appsSidebar'
      ),
    // Expanded = the sidebar this icon speaks for is on screen.
    'aria-expanded': (el, s) =>
      (_sidebarMode(el, s) === 'page' ? !_sidebarHidden(el, s.root) : !!s.root?.[el.scope.RAIL_KEY])
        ? 'true'
        : 'false',
    // The house active look (selected fill + title ink) marks the OPEN apps
    // rail — an overlay you close again. A page's nav is part of the page,
    // so the icon stays quiet while it shows.
    background: (el, s) =>
      _sidebarMode(el, s) === 'rail' && s.root?.[el.scope.RAIL_KEY] ? 'selected' : 'transparent',
    color: (el, s) =>
      _sidebarMode(el, s) === 'rail' && s.root?.[el.scope.RAIL_KEY] ? 'title' : 'inactive',
    // A page's own sidebar: the window with its column on the sidebar's side
    // (right beside a left navbar — functions/navbarLayout.js — when the page
    // opted into the side rule; its own sidebar answers).
    Icon: {
      subscribeTo: ['_homeLook', 'userPrefs', '_isPhoneViewport', SLOT_KEY, '_slotNudge'],
      name: (el, s) => {
        const navs = _pageSidebarsOf(el.parent, s.root)
        return navs.length && _sidebarPosture(el, s.root, navs[0]).side === 'right'
          ? 'sidebarRightFill'
          : 'sidebarLeftFill'
      }
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = s.root
      if (!root) return
      if (_pageSidebarsOf(el, root).length) {
        _toggleSidebar(el, root)
        return
      }
      root.update({ [el.scope.RAIL_KEY]: !root[el.scope.RAIL_KEY] })
    }
  }
}

// The rail's data: the SAME app-group source the search dropdown uses
// (searchAppGroupsFor — availability-checked like _slotEntryMeta, so canvas
// standalone simply shows no rail), filtered to routes the slot can actually
// host: canvas surfaces and full-page settings surfaces gate out through the
// registered isCanvasRoute/isFullPageRoute classifiers — the same predicate
// pair searchAppTileMenu's "Open in side view" row uses.
const _railGroups = (el, root) => {
  const fns = el.context?.functions || {}
  const methods = el.context?.methods || {}
  const groupsFn = fns.searchAppGroupsFor || methods.searchAppGroupsFor
  if (typeof groupsFn !== 'function') return []
  let groups = []
  try {
    groups = groupsFn.call(el, root) || []
  } catch (_) {
    return []
  }
  const isCanvas = fns.isCanvasRoute || methods.isCanvasRoute
  const isFull = fns.isFullPageRoute || methods.isFullPageRoute
  const hostable = (route) => {
    if (!route) return false
    try {
      if (typeof isCanvas === 'function' && isCanvas.call(el, route)) return false
      if (typeof isFull === 'function' && isFull.call(el, route)) return false
    } catch (_) {
      return false
    }
    return true
  }
  return groups
    .map((g) => ({
      label: g?.label || '',
      apps: (g?.apps || []).filter((a) => a && hostable(a.route))
    }))
    .filter((g) => g.apps.length)
}

// One rail row — the launcher tile's { title, icon, iconBg, route } contract
// as a compact list row: gradient squircle + label. Active (this pane's
// current path) gets the house `selected` + title pattern. Clicking opens
// the app IN THIS PANE — pane-scoped by construction — and the pane Slot's
// recorder files it into this pane's history.
const _railRow = (SLOT_KEY) => {
  return {
    tag: 'button',
    type: 'button',
    flow: 'x',
    align: 'center',
    gap: 'Z2',
    padding: 'Y Z2',
    round: 'radiusControl',
    width: '100%',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    // No transition on the background — it's a reactive factory (subscribeTo
    // re-runs it every tick the slot key changes), and a `background`
    // transition on a factory-driven row background gets restarted on every
    // re-application (framework constraint, DESIGN_LANGUAGE.md §7.7 / §11.3).
    transition: '140ms defaultBezier color',
    subscribeTo: [SLOT_KEY],
    // Deliberately inlined twice rather than a shared `active(el, s)` local —
    // frank tries to lift a locally-declared helper referenced from more
    // than one serialized factory in this same returned object, and (unlike
    // the plain data reads elsewhere in this file) that lift path doesn't
    // resolve calls through `el.scope` cleanly. Same call already made for
    // `prompt.js`'s 4 copies of `_formatCodeBlock` — small, deliberate
    // duplication beats a frank-hostile shared closure.
    background: (el, s) =>
      !!s.route && canvasCleanHref(s.root?.[el.scope.SLOT_KEY]) === canvasCleanHref(s.route)
        ? 'selected'
        : 'transparent',
    color: (el, s) =>
      !!s.route && canvasCleanHref(s.root?.[el.scope.SLOT_KEY]) === canvasCleanHref(s.route)
        ? 'title'
        : 'caption',
    ':hover': { background: 'hover', color: 'title' },
    // `s.title` is a polyglot KEY carried in state (shell.launcher.*.title), so
    // it has to be RESOLVED before it reaches the label — interpolating it raw
    // shipped `Open shell.launcher.notes.title` to the a11y tree on eleven
    // tiles. The verb comes from its own key with a {name} hole, never
    // concatenated: a translation orders those two parts its own way.
    //
    // The name in it is the Label's own (below): THE store-copy resolver.
    'aria-label': (el, s) =>
      s.title
        ? el
            .call('polyglot', 'canvas.slot.openApp')
            .replace('{name}', el.call('storeItemText', { key: s.appKey, name: s.title }, 'name'))
        : el.call('polyglot', 'canvas.slot.openAppGeneric'),
    // THE calm app glyph (the shell's AppGlyphTile — 9A, "calm glyph tiles
    // instead of bright gradients"): the same plate the search dropdown and
    // the home App strip wear, at the row's compact size. The raw-hex
    // gradient squircles this row used to paint are gone.
    Tile: {
      extends: 'AppGlyphTile',
      boxSize: 'B1',
      minWidth: 'B1',
      Icon: { name: (el, s) => s.icon || 'layers', boxSize: 'Z' }
    },
    // The app's NAME — primary text, never cut (the overflow law,
    // HOME_AMBIENT_SKIN_SPEC §3b rule 8): it wraps beside its tile.
    Label: {
      tag: 'span',
      flex: '1 1 auto',
      minWidth: '0',
      overflowWrap: 'anywhere',
      fontSize: 'Z',
      fontWeight: '700',
      // i18n — resolved through THE store-copy resolver
      // (shared/functions/storeItemText.js), the call the navbar tab and the
      // apps launcher make, so an app has one name on every surface (Nika
      // 2026-10-23: "app names = what a person sees in the workspace App
      // Store"). marcus's 390 px walk (2026-10-24) read this rail as "Store,
      // Catalog, CMS, Analytics, Warehouse" — each app manifest's tile title —
      // beside an App Store that says Commerce, Products, Content, Insights,
      // Inventory. Its source order serves every tile with no branch here:
      //   • an INSTALLED APP's tile carries `appKey` and reads the store's name;
      //   • a HOUSE tile's title is a polyglot key (the VIEWS rows printed
      //     `shell.viewNav.apps` before a resolver call landed here) and
      //     resolves at the resolver's last source, which is polyglot;
      //   • an extension's or a customer's own title passes through unchanged.
      text: (el, s) => el.call('storeItemText', { key: s.appKey, name: s.title || '' }, 'name')
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const root = s.root
      if (!root || !s.route) return
      root.update({ [el.scope.SLOT_KEY]: s.route })
    }
  }
}

// ── The apps rail — in-sheet quick access to every slot-hostable app ───────
//
// A slide-over INSIDE the sheet: absolutely positioned over the pane's
// content row, glass like the slot chrome, borderless (fill-contrast
// separation per design-direction — no hairline, no second shadow), sliding
// on a 200ms defaultBezier transform/opacity pair. It stays MOUNTED while
// closed (transform + opacity + pointerEvents quiet it) so the slide can
// play both ways — a `hide:` gate would display:none it mid-exit. Its own
// header floats sticky above the scrolling list on a backdrop blur ("
// navigation floating above it").
const _slotRail = (SLOT_KEY) => {
  const RAIL_KEY = _RAIL_KEYS[SLOT_KEY]
  return {
    // Immersive module members never get the apps switcher (matches the
    // RailToggleBtn gate above) — a persisted open RAIL_KEY must not render
    // a panel whose toggle is hidden.
    hide: (el, s) => {
      const imm = el.context?.functions?.moduleImmersiveActive
      return typeof imm === 'function' && imm(s.root)
    },
    position: 'absolute',
    top: '0',
    left: '0',
    bottom: '0',
    zIndex: '3',
    width: 'G',
    minWidth: '0',
    maxWidth: '82%',
    flow: 'y',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    theme: 'dialog',
    round: 'radiusCard',
    shadow: 'float',
    margin: 'Z',
    borderWidth: '0',
    transition: 'B defaultBezier transform, B defaultBezier opacity',
    // RAIL_KEY read via `el.scope.RAIL_KEY` (pane-level scope) rather than
    // closing over the local above — see _histBtn's note.
    transform: (el, s) => (s.root?.[el.scope.RAIL_KEY] ? 'translateX(0)' : 'translateX(-108%)'),
    opacity: (el, s) => (s.root?.[el.scope.RAIL_KEY] ? '1' : '0'),
    pointerEvents: (el, s) => (s.root?.[el.scope.RAIL_KEY] ? 'auto' : 'none'),
    // Offscreen controls must leave keyboard navigation too. Inert preserves
    // the slide animation and mounted state while excluding every descendant.
    inert: (el, s) => !s.root?.[el.scope.RAIL_KEY],
    'aria-hidden': (el, s) => (s.root?.[el.scope.RAIL_KEY] ? 'false' : 'true'),
    subscribeTo: [RAIL_KEY, SLOT_KEY, 'workspaceProject', 'workspaceSettings'],

    // The floating navigation strip: sticky over the list, backdrop glass.
    RailHead: {
      position: 'sticky',
      top: '0',
      zIndex: '1',
      flexShrink: '0',
      flow: 'x',
      align: 'center',
      padding: 'A A Z A',
      theme: 'dialog',
      Label: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: '{{ canvas.pageSlot.apps | polyglot }}'
      }
    },

    Groups: {
      flow: 'y',
      gap: 'A',
      padding: 'Z A A A',
      // The children-factory OWNER subscribes to the signals its rows read:
      // SLOT_KEY drives each row's active paint (verified live 2026-08-06 —
      // with the subscription only on the outer rail, a slot change never
      // re-ran the rows and the active highlight went stale), and the
      // workspace signals refresh the group list itself when a module's
      // apps land/change.
      subscribeTo: [SLOT_KEY, 'workspaceProject', 'workspaceSettings'],
      childrenAs: 'state',
      children: (el, s) => _railGroups(el, s.root),
      childExtends: {
        flow: 'y',
        gap: 'Y',
        GroupLabel: {
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '700',
          color: 'caption',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          paddingLeft: 'Z',
          // i18n — the rail reads the SAME group data the apps launcher does
          // (searchAppGroupsFor), so it needs the same one call: a shipped
          // house group carries a polyglot KEY, while the workspace group's
          // label is the customer's workspace NAME. `translate()` resolves the
          // first and returns the second unchanged.
          text: (el, s) => el.call('polyglot', s.label || '')
        },
        Rows: {
          flow: 'y',
          gap: 'V',
          childrenAs: 'state',
          children: (el, s) => s.apps || [],
          childExtends: _railRow(SLOT_KEY)
        }
      }
    }
  }
}

// ── Pane factory ───────────────────────────────────────────────────────────
//
// Both panes are instances of this one recipe — the only differences are which
// state key drives them and the primary's extra duties (?page= URL sync,
// fullscreen toggle, second-pane promotion on close).
// ── The window's WIDTH (Nika 2026-09-29: "why did you decrease width of
// pageslot? maybe increase a bit so at least it takes up to 60-70% and add
// option to resize in full") ─────────────────────────────────────────────────
//
// Pick 10 A (4e67753e2) capped one floating window at the brand's wide overlay
// (`overlayXl`, 880 px) on the FRAME: at 1440 px the page went from the whole
// band (~1416 px) to 880 px, at 2560 px to a third of the screen. Now the
// frame stays the band — --slot-top, --slot-bottom, the X2 gutters and every
// shell rule that moves them — and the SHEET (one window in the default
// floating state) is a share of that band, sized on the pane:
//   • the full available width by default (`_SHEET_DEFAULT_PCT`);
//   • never narrower than the brand's `overlayXl` while the band has room for
//     it (the old sheet is the floor), never wider than the band;
//   • the resize handle on its right edge changes the share live, by pointer
//     or keyboard; a double-click or Enter on it returns to the default;
//   • the header's full-screen control still takes it edge to edge.
// A split, a full-screen window, the module presentations and the second pane
// keep filling their share of the band; a phone keeps its full-bleed sheet
// (the band is narrower than the floor there, so the sheet is the band).
//
// THE SHARE IS THE PERSON'S (user_preferences `slotSheetWidth`, a percent of
// the band — the same share on every screen): this session's decision
// (`root._slotSheetWidth`) first, then the record once its row has loaded,
// then this device's copy (it paints before the row arrives), then the
// default. The shell writes it (workspace functions/slotSheetWidth.js) under
// the home-prefs durability law; a bundle without that writer keeps the
// choice for the session.
const _SHEET_DEFAULT_PCT = 100
const _SHEET_MIN_PCT = 30
const _SHEET_STEP_PCT = 2
const _SHEET_BIG_STEP_PCT = 10
const _SHEET_CACHE_KEY = 'symbols_slot_sheet_w'

const _sheetPctOk = (v) => typeof v === 'number' && isFinite(v) && v >= _SHEET_MIN_PCT && v <= 100

// One window in the default floating state — the only state the share sizes.
const _isSheet = (el, root) => {
  const m = _slotModes(el, root)
  return !m.split && !m.fullscreen && !m.imm && !m.rail
}

const _sheetPct = (root) => {
  if (!root) return _SHEET_DEFAULT_PCT
  if (_sheetPctOk(root._slotSheetWidth)) return root._slotSheetWidth
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  if (prefs) return _sheetPctOk(prefs.slotSheetWidth) ? prefs.slotSheetWidth : _SHEET_DEFAULT_PCT
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(_SHEET_CACHE_KEY) : null
    const v = raw == null || raw === '' ? NaN : Number(raw)
    if (_sheetPctOk(v)) return v
  } catch (_) {
    /* no device copy — the default */
  }
  return _SHEET_DEFAULT_PCT
}

// The floor: the brand's wide overlay, read from the design system the shell
// runs (the brand fills `sizes` through the shared-library merge) — a token
// value, never a literal here. Without it (a bundle without the brand sizes)
// the share alone applies.
const _sheetFloor = (el) => {
  const sizes = el && el.context && el.context.designSystem && el.context.designSystem.sizes
  const v = sizes && sizes.overlayXl
  return typeof v === 'string' && /^\d+(\.\d+)?(px|rem|em)$/.test(v.trim()) ? v.trim() : null
}

// The pane's width as a sheet: the share, lifted to the floor while the band
// has room for it, never past the band.
const _sheetWidthCss = (el, root) => {
  const pct = _sheetPct(root)
  const floor = _sheetFloor(el)
  return floor ? `min(100%, max(${floor}, ${pct}%))` : `${pct}%`
}

// The band the share is measured against: the frame, the handle's pane's
// parent. A layout read, for a drag and a key only.
const _sheetBand = (handle) => {
  const node = handle && handle.parent && handle.parent.parent && handle.parent.parent.node
  const w =
    node && typeof node.getBoundingClientRect === 'function'
      ? node.getBoundingClientRect().width
      : 0
  return w > 0 ? w : 0
}
const _sheetMinPct = (handle, band) => {
  const floor = _sheetFloor(handle)
  const px = floor && /px$/.test(floor) ? parseFloat(floor) : 0
  const floorPct = band > 0 && px > 0 ? Math.min(100, (px / band) * 100) : 0
  return Math.max(_SHEET_MIN_PCT, floorPct)
}
const _sheetClamp = (pct, min) => Math.round(Math.max(min, Math.min(100, pct)) * 10) / 10

// Save the person's choice through the shell's writer — this session at once,
// then the record. A bundle without it keeps the choice for this session, on
// the session key only (never on a copy of the preferences row).
const _saveSheetPct = (el, root, value) => {
  if (typeof el.context?.functions?.setSlotSheetWidth === 'function') {
    el.call('setSlotSheetWidth', value)
    return
  }
  root.update({ _slotSheetWidth: value }, { preventFetch: true })
}

// A drag: the pointer is captured on the handle, so every move and the release
// land here. The leading edge stays fixed; the width follows the pointer
// one for one. The share updates live on the session key; the
// release remembers it once. A cancel (Escape, a lost capture) puts back the
// share the drag started from.
const _sheetDragStart = (e, el, s) => {
  if (!e || e.button !== 0 || !s.root) return
  const band = _sheetBand(el)
  const pane = el.parent && el.parent.node
  const w =
    pane && typeof pane.getBoundingClientRect === 'function'
      ? pane.getBoundingClientRect().width
      : 0
  if (!(band > 0) || !(w > 0)) return
  e.preventDefault()
  e.stopPropagation()
  el._sheetDrag = {
    x: e.clientX,
    w,
    band,
    min: _sheetMinPct(el, band),
    was: s.root._slotSheetWidth,
    moved: false,
    id: e.pointerId
  }
  try {
    if (el.node && e.pointerId != null) el.node.setPointerCapture(e.pointerId)
  } catch (_) {
    /* no capture — the moves still land while the pointer is on the handle */
  }
}
const _sheetDragMove = (e, el, s) => {
  const d = el._sheetDrag
  if (!d || !s.root) return
  e.stopPropagation()
  const pct = _sheetClamp(((d.w + e.clientX - d.x) / d.band) * 100, d.min)
  if (pct === s.root._slotSheetWidth) return
  d.moved = true
  s.root.update({ _slotSheetWidth: pct }, { preventFetch: true })
}
const _sheetDragEnd = (e, el, s, cancelled) => {
  const d = el._sheetDrag
  if (!d) return
  el._sheetDrag = null
  try {
    if (el.node && d.id != null) el.node.releasePointerCapture(d.id)
  } catch (_) {
    /* already released */
  }
  if (!s.root || !d.moved) return
  if (cancelled) {
    s.root.update({ _slotSheetWidth: d.was == null ? null : d.was }, { preventFetch: true })
    return
  }
  _saveSheetPct(el, s.root, s.root._slotSheetWidth)
}
// The keys (WAI-ARIA window splitter): Left / Right step the share, Shift a
// longer step, Home / End to the bounds, Enter back to the default. Escape
// cancels a drag in flight and is spent; with no drag it is the window's own.
const _sheetKey = (e, el, s) => {
  const root = s.root
  if (!root || !e) return
  if (e.key === 'Escape') {
    if (!el._sheetDrag) return
    e.preventDefault()
    e.stopPropagation()
    _sheetDragEnd(e, el, s, true)
    return
  }
  if (e.key === 'Enter') {
    e.preventDefault()
    e.stopPropagation()
    _saveSheetPct(el, root, null)
    return
  }
  const band = _sheetBand(el)
  const min = _sheetMinPct(el, band)
  // Step from the share on screen: a choice under the floor shows the floor.
  const cur = Math.max(_sheetPct(root), min)
  const step = e.shiftKey ? _SHEET_BIG_STEP_PCT : _SHEET_STEP_PCT
  let next
  if (e.key === 'ArrowRight') next = cur + step
  else if (e.key === 'ArrowLeft') next = cur - step
  else if (e.key === 'Home') next = min
  else if (e.key === 'End') next = 100
  else return
  e.preventDefault()
  e.stopPropagation()
  next = _sheetClamp(next, min)
  if (next !== cur) _saveSheetPct(el, root, next)
}

const _slotPane = (SLOT_KEY, { primary, placeholderKey } = {}) => ({
  // Runtime-visible mirror of this factory's own closure params (2026-08-12,
  // tickets/opus.md "~800 project-side Frankability defects" — canvas's
  // PageSlot factory-closure-capture class, 62 unresolvable-scope-read + 2
  // unresolvable-identifier findings via `smbls frank to-json .`). Every
  // nested prop-factory in this pane's whole subtree (Header, BackBtn,
  // ForwardBtn, RailToggleBtn, AppsRail + its rows, Slot, SplitPlaceholder…)
  // reads SLOT_KEY/RAIL_KEY/HIST_KEY/primary/placeholderKey from
  // `el.scope` — inherited by every descendant that doesn't declare its own
  // `scope` (element/src/create.js's scope-chain: own → parent.scope → … →
  // globalScope) — instead of closing over these JS locals. A closure over a
  // factory param/local doesn't survive frank.toJSON: it lifts each reactive
  // prop into a standalone function, and a free variable that is neither
  // that function's own parameter nor a module-scope const resolves to
  // nothing at eval-revival time — a guaranteed ReferenceError/undefined the
  // moment PageSlot is served through frank's runtime. `scope:` is the
  // framework's sanctioned fix for exactly this (same mechanism as the
  // FA204 precedent in CanvasPromptTextarea.js's `scope.SIZES`), declared
  // ONCE here so every descendant factory below can read it.
  scope: {
    SLOT_KEY,
    RAIL_KEY: _RAIL_KEYS[SLOT_KEY],
    HIST_KEY: _HIST_KEYS[SLOT_KEY],
    primary: !!primary,
    placeholderKey: placeholderKey || null
  },
  // The frame is pointerEvents:none so closed/empty regions never block the
  // page — each pane re-enables interaction for itself.
  pointerEvents: 'auto',
  role: 'region',
  'aria-labelledby': (el) => `page-slot-title-${el.scope.SLOT_KEY}`,
  // The skip link's target while this pane holds a route (workspace
  // functions/skipToMainHref.js): the shell's first tab stop jumps into the
  // window on screen, not to the page behind it.
  id: (el) => `page-slot-pane-${el.scope.SLOT_KEY}`,
  // The pane takes the keyboard when it opens (`_focusPaneIn`) — focusable by
  // script, never a tab stop of its own. The brand reset's [tabindex]
  // :focus-visible ring is a box-shadow that would REPLACE the window's float
  // elevation after a keyboard open, so the sheet keeps its own shadow there
  // (AppDialog's Panel makes the same call).
  tabindex: '-1',
  ':focus-visible': { boxShadow: 'var(--shadow-float)' },
  // The one motion recipe grows the sheet from its bottom edge — it sits on
  // the dock and rises from it (`_PANE_ENTER`). Static: it only matters while
  // a WAAPI run or the phone drag carries a transform.
  transformOrigin: 'center bottom',
  flex: '1 1 0',
  minWidth: '0',
  minHeight: '0',
  flow: 'y',
  overflow: 'hidden',
  // The window is an overlay-level sheet (white-label redesign, DESIGN_SPEC
  // §6.1 "Window"): `radiusSheet` on all four corners — the same radius as
  // every modal, side panel and phone sheet, one clean step above the
  // in-page `radiusCard`.
  round: 'radiusSheet',
  // ONE ground, very consistent (Nika 2026-09-01): the pane paints the
  // SAME `var(--stage-bg, var(--color-stage))` chain the shell root,
  // MainColumn, the main page and every canvas Stage paint
  // (canvasBackground.js STAGE_BACKGROUND, BF-WIDGET-INK-1) — the pane can
  // never differ from the board beside it, in either scheme.
  // 'pane-ground' (brand) carries the chrome only: inks + blur + the one
  // float shadow — it paints no background.
  // The pane ground IS the content-surface family (Nika 2026-09-01 "make
  // it darker, and pageslot and content inside should have same bg"):
  // page sheets paint gray2-family fills in dark and gray16-family in
  // light, so the pane paints the same pair and the frame, gutters and
  // content read as ONE surface — while the stage board (gray5/milk)
  // frames it with real contrast.
  // THE PAIR IS A LIGHT BASE + A '@dark' BLOCK (2026-09-30, Nika
  // "background missing on other Windows devices"). It used to be ONE inline
  // declaration built on the CSS function that picks by colour scheme;
  // Chrome / Edge before 123 do not know that function and drop the WHOLE
  // declaration, so the window had no ground at all. The scheme block
  // compiles to `[data-theme="dark"] .cls` — the root attribute the
  // framework stamps for light, dark and auto — with no browser-version
  // dependency (workspace/__tests__/noLightDarkInShellStyles.test.js). The
  // two tokens are plain colours, not a scheme pair, so nothing splits them.
  // '--stage-bg' rides the inline style (custom props in a style object
  // hit the DOM style attribute directly — the `vars:` spelling measurably
  // never reached the node). Dissolving the chain's first var here resolves the
  // AppPage-family wrappers' own INLINE `var(--stage-bg, ...)` paints to
  // transparent for every descendant — an inline paint beats any
  // slot-scoped class rule, but its var still resolves through the
  // cascade, so the pane sheet becomes the one ground (measured
  // 2026-09-01: a 1896x779 gray5 wrapper behind CalMain). Modules' custom
  // --stage-bg boards flatten to the sheet inside the pane too — intended.
  //
  // THE SHARE OF THE BAND (the width block above): as the one floating window
  // the primary pane fills the available band by default. A chosen narrower
  // width keeps its leading edge beside the navigation; in every other state
  // (a split, full screen, the module presentations) and as the second pane
  // it fills its flex share as before. Per-key factories — each one its own
  // live effect — so a drag moves the width on every step.
  background: 'gray16',
  '@dark': { background: 'gray2' },
  style: {
    '--stage-bg': 'transparent',
    flex: (el, s) => {
      if (el.scope.primary && _isSheet(el, s.root)) return '0 0 auto'
      if (!s.root?._canvasSlotSecond && !s.root?._slotSplitIntent) return '1 1 0'
      const ratio = _splitRatio(s.root)
      return `${(el.scope.primary ? ratio : 100 - ratio) / 50} 1 0`
    },
    width: (el, s) =>
      el.scope.primary && _isSheet(el, s.root) ? _sheetWidthCss(el, s.root) : 'auto',
    marginLeft: () => '0',
    marginRight: (el, s) => (el.scope.primary && _isSheet(el, s.root) ? 'auto' : '0')
  },
  // The containing block of the resize handle (its right edge).
  position: 'relative',
  theme: 'pane-ground',
  borderWidth: '0',
  shadow: 'float',

  // Phone: the pane fills a full-bleed frame, so only the TOP corners round —
  // the other three edges ARE the viewport (iOS sheet). Compound media rules
  // are emitted after flat ones by @symbo.ls/css, so this always wins the
  // cascade over the flat `round` above at ≤768.
  //
  // …and the bottom edge IS the home indicator's swipe zone on an iPhone
  // without a home button (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): a
  // TRANSPARENT bottom border the size of the inset keeps the page's last row
  // and its actions above it, while the pane's own ground paints under the
  // border to the edge (background-clip is the border box). 0 wherever the
  // device reports no inset — the same rule as the AppDialog phone sheet.
  '@mobileL': {
    round: 'radiusSheet radiusSheet 0 0',
    borderBottomWidth: 'env(safe-area-inset-bottom)',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent'
  },

  // Pane identity stamp — the anchor slotPaneKeyFor (shared/functions/
  // slotPane.js) and the local _paneOf walk to, replacing fixed parent hops.
  // onCreate lands it before any child (or keep-alive host) can ask;
  // onRender re-stamps defensively (idempotent one-liner).
  onCreate: (el) => {
    el._slotPaneKey = el.scope.SLOT_KEY
  },
  onRender: (el) => {
    el._slotPaneKey = el.scope.SLOT_KEY
  },

  // Phone drag-to-dismiss. DOMQL owns all four registrations (FA503); the
  // gesture itself is built lazily by `_sheetDragHandlers` on the first
  // event, which is also where the coarse-pointer gate runs (a fine pointer
  // caches a `null` and never re-asks). `?.` — a desktop pane has no
  // handlers, and every one of these fires on every pointer event there.
  onPointerdown: (e, el) => _sheetDragHandlers(el)?.onDown(e),
  onPointermove: (e, el) => _sheetDragHandlers(el)?.onMove(e),
  onPointerup: (e, el) => _sheetDragHandlers(el)?.onUp(e),
  onPointercancel: (e, el) => _sheetDragHandlers(el)?.onUp(e),

  // Visibility + transition interception. The factory is the only hook that
  // provably runs BEFORE the display flip (onUpdate fires after display:none
  // is applied; onBeforeUpdate does not fire on subscribeTo-driven re-renders
  // — see the OPEN tickets in tickets/smbls.md), so open/close choreography
  // hangs off it:
  //   • closed→open: mark shown + pre-hide inline (no-blink) — the entrance
  //     itself starts in onUpdate/onCreate once the node exists.
  //   • open→closed: kick the exit and HOLD the pane via `el._closing`.
  // The `_slotNudge` read subscribes this effect to the close-finish nudge —
  // element-local `_closing` can't re-trigger it on its own.
  hide: (el, s) => {
    void s.root?._slotNudge
    // "Open" for a real mounted route OR, on the split pane, while a split is
    // being armed (placeholderKey) — the empty pane then shows a placeholder so
    // the split telegraphs before an app is picked (2026-07-16 per Nika).
    const route = s.root?.[el.scope.SLOT_KEY]
    const hasRoute = !!route
    const open = hasRoute || (el.scope.placeholderKey ? !!s.root?.[el.scope.placeholderKey] : false)
    // A placeholder pane whose route just ARRIVED (the split pick landed):
    // the pane was already shown, so no open edge fires — read here, before
    // the edges below move `_paneShown`.
    const routeArrived = !!el._paneShown && open && hasRoute && !el._hadRoute
    const routeChanged = !!el._paneShown && hasRoute && route !== el._focusRoute
    el._hadRoute = open && hasRoute
    el._focusRoute = route
    if (routeArrived) {
      el._returnFocus = _focusOrigin(el)
    }
    if (routeChanged) {
      // After the pick's own writes settle (the search closes and hands focus
      // back first), the keyboard follows the new window. This also covers
      // restoring from the dock over an already-open pane: its chip disappears,
      // but there is no open edge. Keep the original return target; focus
      // already inside the pane and an open header search still win.
      queueMicrotask(() => _focusPaneIn(el))
    }
    if (open && !el._paneShown) {
      el._paneShown = true
      // Where the keyboard goes back to when this pane closes — read at the
      // open edge, before anything moves focus. Not for the split pane's
      // placeholder: that open belongs to the search, which holds focus.
      el._returnFocus = hasRoute ? _focusOrigin(el) : null
      if (el.node && el.node.style) el.node.style.opacity = '0'
      _queueEnter(el)
    } else if (!open && el._paneShown) {
      el._paneShown = false
      _paneExit(el, s.root)
    } else if (
      open &&
      el._paneShown &&
      !el._enterQueued &&
      !el._anim &&
      !el._closing &&
      // …and not while a finger owns the transform: the self-heal below clears
      // inline styles, which mid-drag would snap the sheet out from under it.
      !el._dragging
    ) {
      // Self-heal invariant: an open pane with no run in flight sits at base
      // styles — clears an inline pre-hide stranded by any path the runs
      // didn't cover, so a stuck state can never outlive the next state touch.
      _clearInline(el)
    }
    return !open && !el._closing
  },
  // The open/close choreography is driven entirely from the hide factory (the
  // factory auto-tracks every signal it reads — _slotNudge included); the
  // element-level subscription just guarantees a re-render applies the flip.
  subscribeTo: [SLOT_KEY, '_slotSplitRatio', ...(placeholderKey ? [placeholderKey] : [])],

  // iOS grabber — the phone sheet's drag affordance, centred on its top edge.
  // Decorative by design: the header's Close button is the accessible,
  // keyboard-reachable dismissal, so the pill stays out of the a11y tree
  // instead of duplicating it. Hidden off-phone by a flat `display: none` that
  // the `@mobileL` compound rule overrides (flat rules are emitted first by
  // @symbo.ls/css, compound second — the media query always wins).
  GrabHandle: {
    attr: { 'data-sheet-grip': '' },
    'aria-hidden': 'true',
    display: 'none',
    flexShrink: '0',
    align: 'center center',
    padding: 'Y 0 0 0',
    cursor: 'grab',
    // touchAction rides the same phone gate as the Header's: without it the
    // browser claims the vertical pan and the sheet never follows the finger.
    '@mobileL': { display: 'flex', touchAction: 'none' },
    // House tap-slop: grow the HIT area to a thumb without growing the pill.
    '@coarse': {
      position: 'relative',
      ':after': { content: '""', position: 'absolute', inset: 'tapSlop' }
    },
    Grip: {
      width: 'B2',
      height: 'X1',
      round: 'X',
      background: 'caption.35'
    }
  },

  Header: {
    // Immersive module chrome: the pane utility bar (back/fwd/title/close)
    // is shell chrome too — hidden with the rest of it; the module's own
    // rail owns navigation and there is nothing behind to close back to
    // (the close path reopens the landing anyway, see main.js _slotOnClose).
    // EXCEPT with a persistent module rail (chrome.rail): the rail keeps a
    // landing to return to, and immersive members would otherwise have NO
    // way to close an open page (2026-08-14 per Nika "keep pageslot header
    // for non-admin so we can close the page") — rail-active modules keep
    // the header for every persona.
    hide: (el, s) => {
      const railFn = el.context?.functions?.moduleRailActive
      if (typeof railFn === 'function' && railFn(s.root)) return false
      const imm = el.context?.functions?.moduleImmersiveActive
      return typeof imm === 'function' && imm(s.root)
    },
    // The whole header is grip too — a downward flick anywhere on it dismisses,
    // so the gesture doesn't demand a 36px target.
    attr: { 'data-sheet-grip': '' },
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    minWidth: '0',
    maxWidth: '100%',
    // Compact bar (2026-08-31 per Nika "reduce size of pageSlot header,
    // narrow spacings"; second pass 2026-09-01 "make it less space
    // consuming"): the 36px button circle is pinned by the F2
    // one-close-button contract (shared PageSlotHeaderBtn), so compactness
    // comes from the bar's own inset + gap, not the control scale. Zero
    // vertical inset makes the circle define the bar height outright
    // (44px → 36px); the icon's own 10px ring inside the transparent
    // circle keeps the visual breathing room at the pane's top edge.
    // Third pass 2026-09-23 per Nika "add same padding top on pageslot header
    // as left and right": the top inset now equals the side inset (Y); the
    // bottom stays zero so the bar keeps its compact height below the title.
    gap: 'Y',
    padding: 'Z Z Y',
    flexShrink: '0',
    // Phone only: the grabber above now owns the sheet's top padding, and the
    // header must yield the vertical pan to the dismissal gesture.
    '@mobileL': { paddingTop: 'X', touchAction: 'none' },

    // ── Left cluster: back · forward · apps rail — BEFORE the title
    // (2026-08-06 per Nika "add it on the left side before pageslot title…
    // so left arrpw, right arrow, sidebar toggle"). Same PageSlotHeaderBtn
    // recipe as the right cluster; strictly pane-scoped (this pane's
    // history/slot/rail keys only — the split-view contract).
    BackBtn: _histBtn(SLOT_KEY, 'back'),
    ForwardBtn: _histBtn(SLOT_KEY, 'forward'),
    RailToggleBtn: _railToggleBtn(SLOT_KEY),

    Title: {
      tag: 'h1',
      id: (el) => `page-slot-title-${el.scope.SLOT_KEY}`,
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      margin: '0',
      flex: '1 1 0',
      minWidth: '0',
      // The window's NAME — primary text, never cut (overflow law rule 8):
      // it wraps, and the bar (flex-wrap, no fixed height) grows with it.
      overflowWrap: 'anywhere',
      // A phone needs a readable app name as well as touch-sized controls.
      // Keep the title above the actions; every control can still wrap when
      // the available pane width is smaller than their combined targets.
      '@mobileL': { order: '-1', flexBasis: '100%', padding: 'X Y 0' },
      text: (el, s) => {
        const path = s.root?.[el.scope.SLOT_KEY]
        if (!path && el.scope.placeholderKey && s.root?.[el.scope.placeholderKey])
          return el.call('polyglot', 'canvas.pageSlot.newPane')
        const def = _defFor(el, path, s.root)
        // Short form in the slot chrome; document.title keeps the full
        // helmet form (see shortSlotTitle's note). An idle pane with no
        // resolvable def falls back to the same 'New pane' label the
        // placeholder-armed branch above uses, instead of an empty h1
        // (A11Y-1: two empty h1s on a fresh Home load).
        return (
          shortSlotTitle(el.call('resolvePageMetadataTitle', def?.metadata)) ||
          path ||
          el.call('polyglot', 'canvas.pageSlot.newPane')
        )
      }
    },

    // Open this pane's route as a full page — leaves the slot entirely.
    ExpandBtn: {
      extends: 'PageSlotHeaderBtn',
      'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.openAsFullPage'),
      title: (el) => el.call('polyglot', 'canvas.pageSlot.openAsFullPage'),
      Icon: { name: 'arrowUpRightRect' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        // Definition lookups normalize routes; navigation preserves the
        // selected app surface, record, filters and anchor verbatim.
        const path = s.root?.[el.scope.SLOT_KEY] || ''
        // One-shot reopen suppression — the SAME handshake the CloseBtn's
        // rail case and moduleGoHome use. Without it, on an immersive-module
        // workspace the slot-close watcher's landing reopen fires a tick
        // after this deliberate leave and re-opens the LANDING pane over the
        // full page (measured live on app.bellforge.eu 2026-09-02: /theses
        // full-page + the reopened /assess slot = doubled layout, and the
        // landing def's onInit then re-set the module view to the persona
        // default, which read as "open in new page shows the wrong page").
        if (s.root) s.root._slotSkipReopenOnce = true
        _closeSlot(s.root)
        if (path) el.router(path, el.getRoot())
      }
    },

    // Split-view + fullscreen toggles — single-pane primary only (once a
    // second pane is open the split button is done and fullscreen is moot,
    // so both hide, and the second pane never ships them at all).
    ...(primary
      ? {
          // Open a SECOND pane beside this one (iPad-style split view). Arms
          // `_slotSplitIntent` so the NEXT pick lands in `_canvasSlotSecond`,
          // and opens THE GLOBAL SEARCH to choose it — the header search the
          // whole shell uses, with its own dropdown, rows and keys (Nika
          // 2026-09-28: "it should activate search dropdown, not its own - so
          // we make it consistent"). Every pick spends the intent: an app row
          // through openAppPage, a record row through openInSlot (the one
          // opener). Shown ACTIVE (not hidden) once split so the icon carries
          // the on-state; hidden only on transient AI answer views (nothing to
          // split them against).
          SplitBtn: {
            extends: 'PageSlotHeaderBtn',
            // Shown only where a split can open. Below SPLIT_MIN_WIDTH_PX
            // (1024, functions/searchApps.js) the shell folds every split into
            // the primary pane, so the button would promise a second pane the
            // pick then refuses — a lying control. The shell's
            // `splitBelowFloor` media token is that floor's exact complement
            // (a phone is inside it too: one full-width sheet).
            '@splitBelowFloor': { display: 'none' },
            // Re-render this button when the split signals change so its ON-state
            // paint factories re-run. A plain-button conditional-class (.isActive)
            // needs the InteractiveComponent base PageSlotHeaderBtn lacks, so we
            // paint the fill directly — plain prop factories DO re-run on
            // subscribeTo (the placeholder proves it), unlike the class effect.
            subscribeTo: ['_canvasSlotSecond', '_slotSplitIntent'],
            // Stays visible while split so the icon can carry the ON state; only
            // hidden on transient answer views (nothing to split against) —
            // or when split view / split panes are off at /flags.
            hide: (el, s) => {
              // Immersive module members get a page, not a workspace — split
              // view is shell furniture, admins only (2026-08-14 per Nika
              // "hide app switcher and split view for non admins from
              // pageslot header"). Read FIRST, unconditionally, so the
              // persona flip re-runs this factory (short-circuit ordering
              // would drop the tracked dep).
              const imm = el.context?.functions?.moduleImmersiveActive
              if (typeof imm === 'function' && imm(s.root)) return true
              return (
                !canvasFeatureEnabled(s.root, 'splitView') ||
                !canvasFeatureEnabled(s.root, 'splitView.splitPane') ||
                String(s.root?._canvasSlot || '').startsWith('/answer/')
              )
            },
            // ON state — the house active look (`selected` fill + title text)
            // whenever a second pane is open OR a split is being armed.
            background: (el, s) =>
              s.root?._canvasSlotSecond || s.root?._slotSplitIntent ? 'selected' : 'transparent',
            color: (el, s) =>
              s.root?._canvasSlotSecond || s.root?._slotSplitIntent ? 'title' : 'inactive',
            'aria-pressed': (el, s) =>
              s.root?._canvasSlotSecond || s.root?._slotSplitIntent ? 'true' : 'false',
            'aria-label': (el, s) =>
              el.call(
                'polyglot',
                s.root?._canvasSlotSecond || s.root?._slotSplitIntent
                  ? 'canvas.splitView.close'
                  : 'canvas.splitView.open'
              ),
            title: (el, s) =>
              el.call(
                'polyglot',
                s.root?._canvasSlotSecond || s.root?._slotSplitIntent
                  ? 'canvas.splitView.close'
                  : 'canvas.splitView.open'
              ),
            Icon: { name: 'columns' },
            onClick: (e, el, s) => {
              e.preventDefault()
              e.stopPropagation()
              // Toggle: exit an open (or arming) split, else start one and ask
              // the global search for the second page.
              if (s.root?._canvasSlotSecond || s.root?._slotSplitIntent) {
                s.root.update({
                  _canvasSlotSecond: null,
                  _slotSplitIntent: false,
                  _slotPinned: slotPinAfterSecondLeaves(s.root)
                })
                return
              }
              // THE SEARCHBTN OPEN, plus the intent: the same two writes the
              // header's own search button makes (shared HeaderTabs SearchBtn
              // `openHeaderSearchMorph`) — remember where focus returns, open
              // the search. The header search then focuses its input on that
              // edge (or on the intent's own arrival while it is already
              // open, AppHeader `focusSearchOnOpen`), and the input's focus
              // opens the search dropdown under the pill: the launcher on an
              // empty field, the results on typed text. No dropdown of this
              // button's own — the picker anchored HERE was a second search
              // surface with its own position and width.
              //
              // Cancelling is the search's: Escape and an outside dismissal
              // clear the unused intent (closeNavSearch, the launcher's
              // onClose), which removes the empty pane; this trigger gets
              // focus back.
              //
              // A launcher that was open when this button was pressed has just
              // been closed by that press (the dropdown's outside-press
              // close), and its dismissal stamp would make the search's focus
              // read as that close's own restore and open nothing. This press
              // is a deliberate open, so the stamp goes first. The function is
              // the shell's (functions/searchApps.js): absent elsewhere.
              if (typeof el.context?.functions?.forgetSearchAppsDismissed === 'function') {
                el.call('forgetSearchAppsDismissed')
              }
              if (el.node) s.root.__headerSearchReturnFocusTo = el.node
              s.root.update({
                _slotSplitIntent: true,
                _slotFullscreen: null,
                _headerSearchOpen: true
              })
            }
          },
          FullscreenBtn: {
            extends: 'PageSlotHeaderBtn',
            // The mobile frame is already edge-to-edge in either state.
            '@mobileL': { display: 'none' },
            // B9 (tickets/bellforge.md): immersive members are ALWAYS
            // edge-to-edge (slotFullscreenActive returns true regardless of
            // the flag), so the expand toggle is a lying control for them —
            // hidden. Admins (rail-only chrome) keep it; an admin PREVIEWING
            // a non-admin persona (view-as) gets the truthful immersive
            // chrome and loses it too, same contract as the navbars.
            hide: (el, s) => {
              const fns = el.context?.functions || {}
              const imm =
                typeof fns.moduleImmersiveActive === 'function' && fns.moduleImmersiveActive(s.root)
              if (imm) {
                // B9 rev (Nika 2026-08-16 admin-immersive default): immersive
                // still hides the lying control for MEMBERS and for an admin
                // PREVIEWING a member (view-as) — but a REAL admin's default
                // is now immersive too, and this button is their ONE way to
                // toggle the shell chrome back. Keep it for them.
                const isAdmin =
                  typeof fns.currentUserIsAdmin === 'function' && fns.currentUserIsAdmin(s.root)
                const preview =
                  typeof fns.moduleViewAsActive === 'function' && fns.moduleViewAsActive(s.root)
                if (!isAdmin || preview) return true
              }
              return (
                !canvasFeatureEnabled(s.root, 'splitView') ||
                !canvasFeatureEnabled(s.root, 'splitView.fullscreenMode') ||
                !!s.root?._canvasSlotSecond ||
                !!s.root?._slotSplitIntent
              )
            },
            // ONE name for the toggle (its state rides aria-pressed); the
            // tooltip says what a press does now.
            'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.toggleFullscreen'),
            title: (el, s) => {
              const imm = el.context?.functions?.moduleImmersiveActive
              const on =
                (typeof imm === 'function' && imm(s.root)) || s.root?._slotFullscreen === true
              return el.call(
                'polyglot',
                on ? 'canvas.pageSlot.fullscreenExit' : 'canvas.pageSlot.fullscreenEnter'
              )
            },
            'aria-pressed': (el, s) => {
              const imm = el.context?.functions?.moduleImmersiveActive
              return (typeof imm === 'function' && imm(s.root)) || s.root?._slotFullscreen === true
                ? 'true'
                : 'false'
            },
            Icon: {
              // Exit icon whenever the view is actually edge-to-edge: the
              // rail-admin's immersive DEFAULT (chrome hidden — the toggle
              // exits it) or an explicit fullscreen elsewhere.
              name: (el, s) => {
                const fns = el.context?.functions || {}
                const imm =
                  typeof fns.moduleImmersiveActive === 'function' &&
                  fns.moduleImmersiveActive(s.root)
                return imm || s.root?._slotFullscreen === true ? 'fullscreenExit' : 'fullscreen'
              }
            },
            onClick: (e, el, s) => {
              e.preventDefault()
              e.stopPropagation()
              const fns = el.context?.functions || {}
              const railOn =
                typeof fns.moduleRailActive === 'function' && fns.moduleRailActive(s.root)
              const isAdmin =
                typeof fns.currentUserIsAdmin === 'function' && fns.currentUserIsAdmin(s.root)
              const preview =
                typeof fns.moduleViewAsActive === 'function' && fns.moduleViewAsActive(s.root)
              if (railOn && isAdmin && !preview) {
                // Nika 2026-08-16: for a rail-workspace ADMIN this button
                // toggles the CHROME — immersive default (navbar hidden,
                // everything edge-to-edge, slot open or not) ⇄ shell chrome
                // back. Persisted per workspace; _slotFullscreen resets so
                // the chrome-on state resumes its own defaults.
                const imm =
                  typeof fns.moduleImmersiveActive === 'function' &&
                  fns.moduleImmersiveActive(s.root)
                const nextPref = imm ? 'shell' : null
                if (typeof fns.writeModuleChromePref === 'function')
                  fns.writeModuleChromePref(s.root, nextPref)
                s.root.update({ _moduleChromePref: nextPref, _slotFullscreen: null })
                return
              }
              // Elsewhere: the explicit-fullscreen toggle (BF-PAGESLOT-EXPAND-1).
              const explicit = s.root?._slotFullscreen === true
              const next = explicit ? null : true
              const writePref = el.context?.functions?.writeSlotFullscreenPref
              if (typeof writePref === 'function') writePref(s.root, next)
              s.root.update({ _slotFullscreen: next })
            }
          }
        }
      : {}),

    // Add-to-Workspace — ONLY on an app-build preview (/x-preview/* route,
    // opened by openExtensionPreview when the AI builds an app away from
    // /add-app) whose build is already persisted (metadata.__extensionApp
    // carries the projectId). Promotes the previewed app into the workspace's
    // additive apps: shared + navbar tab + launcher tile
    // (addExtensionToWorkspace owns the confirm + settings write).
    AddToWorkspaceBtn: {
      extends: 'PageSlotHeaderBtn',
      // display-FACTORY, not hide: — the gated hide provably mounted on the
      // element while the button still rendered (route said hidden too), so
      // hide: is not applied for this instance in this runtime. The dock's
      // reactive display pattern is the proven mechanism. Fail-CLOSED +
      // FA515 force-track of the claim keys.
      display: (el, s) => {
        void s.root?.jwtClaims
        void s.root?.user
        void s.root?.org
        const fns = el.context?.functions || {}
        let isAdmin = false
        let preview = false
        try {
          isAdmin = typeof fns.currentUserIsAdmin === 'function' && fns.currentUserIsAdmin(s.root)
          preview = typeof fns.moduleViewAsActive === 'function' && fns.moduleViewAsActive(s.root)
        } catch (_) {
          isAdmin = false
        }
        if (!isAdmin || preview) return 'none'
        const route = String(s.root?.[el.scope.SLOT_KEY] || '')
        if (!route.startsWith('/x-preview/')) return 'none'
        const def = _defFor(el, route, s.root)
        return !def?.metadata?.__extensionApp?.projectId ? 'none' : 'flex'
      },
      'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.addToWorkspace'),
      title: (el) => el.call('polyglot', 'canvas.pageSlot.addToWorkspace'),
      Icon: { name: 'plus' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const def = _defFor(el, s.root?.[el.scope.SLOT_KEY], s.root)
        const app = def?.metadata?.__extensionApp
        try {
          el.call('addExtensionToWorkspace', app)
        } catch (err) {
          console.warn('[PageSlot] add-to-workspace failed:', err)
        }
      }
    },

    // Save-as-app — ONLY on a transient AI answer view (/answer/* route). Promotes
    // the drawn view into a persisted /add-app extension (the bridge from
    // draw-on-fly UI to a workspace app). saveAnswerViewAsApp recovers the source
    // from the answer def's metadata.__source, creates the extension, and opens
    // /add-app where it renders. `hide` re-evaluates on route switch via the
    // pane's subscribeTo:[SLOT_KEY].
    SaveAsAppBtn: {
      extends: 'PageSlotHeaderBtn',
      // display-factory — same conversion + reasoning as AddToWorkspaceBtn.
      display: (el, s) => {
        void s.root?.jwtClaims
        void s.root?.user
        void s.root?.org
        const fns = el.context?.functions || {}
        let isAdmin = false
        let preview = false
        try {
          isAdmin = typeof fns.currentUserIsAdmin === 'function' && fns.currentUserIsAdmin(s.root)
          preview = typeof fns.moduleViewAsActive === 'function' && fns.moduleViewAsActive(s.root)
        } catch (_) {
          isAdmin = false
        }
        if (!isAdmin || preview) return 'none'
        return !String(s.root?.[el.scope.SLOT_KEY] || '').startsWith('/answer/') ? 'none' : 'flex'
      },
      'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.saveAsApp'),
      title: (el) => el.call('polyglot', 'canvas.pageSlot.saveAsApp'),
      Icon: { name: 'save' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const route = s.root?.[el.scope.SLOT_KEY]
        try {
          el.call('saveAnswerViewAsApp', route)
        } catch (err) {
          console.warn('[PageSlot] save-as-app failed:', err)
        }
      }
    },

    // Minimize — park the open app into the MinimizedDock, keep-alive: its
    // host stays mounted (display:none via the reactive predicate) so the
    // dock restore reveals the EXACT same element — scroll, inputs and
    // in-flight work intact. Primary pane only (v1): parked entries always
    // restore into the primary frame, so the split pane keeps its single
    // Close dismissal.
    ...(primary
      ? {
          MinimizeBtn: {
            extends: 'PageSlotHeaderBtn',
            // Immersive member ON the module landing: parking the landing
            // just re-opens it (the landing redirect owns `/`), so the
            // control is circular there — hidden, same rationale as
            // CloseBtn below.
            hide: (el, s) => {
              const imm = el.context?.functions?.moduleImmersiveActive
              if (!(typeof imm === 'function' && imm(s.root))) return false
              const landFn = el.context?.functions?.moduleImmersiveLanding
              const landing = typeof landFn === 'function' ? landFn(s.root) : null
              if (!landing) return false
              return String(s.root?.[el.scope.SLOT_KEY] || '').split('?')[0] === landing
            },
            'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.minimize'),
            title: (el) => el.call('polyglot', 'canvas.pageSlot.minimize'),
            Icon: { name: 'minus' },
            onClick: (e, el, s) => {
              e.preventDefault()
              e.stopPropagation()
              _minimizeSlot(el, s.root)
            }
          }
        }
      : {}),

    // KEEP THIS PANE OPEN — the opt-in pane pin (Nika 2026-10-10: "Yes, opt-in
    // pin"). In BOTH panes. A pinned pane is kept: a navbar or launcher open
    // lands BESIDE it, replacing the other pane (shell functions/searchApps.js
    // openAppPage). The house header button, the brand `pin` glyph, the
    // house on-state paint (same as SplitBtn). Shown only where it can work:
    // two panes fit (the `splitBelowFloor` complement), split view is on, and
    // the pane shows something. The pin moves with its pane on close and
    // minimise (functions/slotPin.js).
    PinBtn: {
      extends: 'PageSlotHeaderBtn',
      '@splitBelowFloor': { display: 'none' },
      subscribeTo: ['_slotPinned', '_canvasSlot', '_canvasSlotSecond'],
      hide: (el, s) => {
        // Read FIRST, unconditionally (same reason as SplitBtn's hide).
        const imm = el.context?.functions?.moduleImmersiveActive
        if (typeof imm === 'function' && imm(s.root)) return true
        return (
          !canvasFeatureEnabled(s.root, 'splitView') ||
          !canvasFeatureEnabled(s.root, 'splitView.splitPane') ||
          !s.root?.[el.scope.SLOT_KEY]
        )
      },
      background: (el, s) =>
        s.root?._slotPinned === el.scope.SLOT_KEY ? 'selected' : 'transparent',
      color: (el, s) => (s.root?._slotPinned === el.scope.SLOT_KEY ? 'title' : 'inactive'),
      'aria-pressed': (el, s) => (s.root?._slotPinned === el.scope.SLOT_KEY ? 'true' : 'false'),
      'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.keepPane'),
      title: (el) => el.call('polyglot', 'canvas.pageSlot.keepPane'),
      Icon: { name: 'pin' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        s.root.update({ _slotPinned: slotPinToggle(s.root, el.scope.SLOT_KEY) })
      }
    },

    CloseBtn: {
      // THE house close affordance (brand CloseButton — one X, one size, for
      // every sheet, panel and window; F2 2026-08-16), not the quiet toolbar
      // circle the other header controls share: the window's X is the filled
      // close of the overlay language (DESIGN_SPEC §5.1 / §4.10), so it moves
      // with every modal's X when the brand restyles it. The tooltip trigger
      // stays; on a coarse pointer the box grows to the touch control, like
      // its toolbar neighbours, so adjacent targets never overlap.
      extends: ['CloseButton', 'TitleTooltipTrigger'],
      marginLeft: 'auto',
      '@coarse': {
        boxSize: 'controlH',
        ':after': { inset: '0' }
      },
      // Always visible — the X is a permanent affordance (2026-08-15 per Nika
      // "pageslot should have X icon dont hide it, and it should close to get
      // back on dashboard"). It never hides, on any page or persona.
      'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.close'),
      title: (el) => el.call('polyglot', 'canvas.pageSlot.close'),
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        // Immersive member: there is no home board behind the slot — the slot
        // IS their whole view — so Close means "back to the dashboard/landing"
        // (Nika). Route the slot straight to the module's landing instead of
        // _dismissPane's dismiss-to-`/` (which re-triggered the landing
        // redirect and read as a no-op ON the landing). On the landing this is
        // a same-value no-op — harmless, and the X stays present.
        //
        // EXCEPT an admin PREVIEWING a non-admin persona (moduleViewAsActive):
        // their real shell still exists behind the sheet, and the landing
        // routing left them with no exit at all ("after choosing different
        // non-admin role — cant close pageslot", Nika 2026-08-15). For the
        // preview, X REALLY closes — one press, sheet gone; the persistent
        // module rail stays for re-entry and the persona switch back.
        // Interrogate the TOP state, not this pane's `s.root` — measured live
        // (bellf-qa, 2026-08-15): on PageSlot's s.root, currentUserIsAdmin
        // resolves FALSE while the real root says TRUE, so moduleViewAsActive
        // (admin-gated) read false here and the preview kept dead-ending on
        // the landing. moduleImmersiveActive only "worked" on the partial
        // root via its not-admin→immersive branch — the same wrong answer
        // with a lucky sign. getRootState() is the element API the rest of
        // canvas already uses for top-state reads.
        const rs = (typeof el.getRootState === 'function' && el.getRootState()) || s.root
        const imm = el.context?.functions?.moduleImmersiveActive
        if (typeof imm === 'function' && imm(rs)) {
          const va = el.context?.functions?.moduleViewAsActive
          const previewing = typeof va === 'function' && va(rs)
          // X CLOSES for immersive members too, when the module declares a
          // rail (Nika: "allow non-admins to close pageslot"). This used to
          // re-route to the module landing for everyone non-admin, on the
          // reasoning that a chromeless member closing the page would be
          // stranded on a bare board with no way back. That reasoning still
          // holds for a module with NO rail — so those keep the re-route
          // untouched — but not for one that declares `chrome.rail`: the
          // shell mounts that rail on every route INCLUDING '/', so the board
          // they land on is fully navigable. Bellforge is the rail case.
          const railFn = el.context?.functions?.moduleRailActive
          const hasRail = typeof railFn === 'function' && railFn(rs)
          if (!previewing && !hasRail) {
            const landFn = el.context?.functions?.moduleImmersiveLanding
            const landing = typeof landFn === 'function' ? landFn(rs) : null
            if (landing) {
              s.root.update({ [el.scope.SLOT_KEY]: landing })
              return
            }
          }
          if (!previewing && hasRail) {
            // One-shot reopen suppression — the SAME handshake moduleGoHome
            // uses (workspaceProjects.js). `_slotOnClose`'s immersive landing
            // reopen is a second writer that would otherwise undo this close a
            // tick later (measured at 11ms), so without this flag the X reads
            // as doing nothing at all. Falls through to the real dismiss below.
            rs._slotSkipReopenOnce = true
          }
        }
        // Non-immersive (admin): iPad semantics on the primary (closing
        // promotes the remaining pane); the split pane closes itself and
        // cancels an in-progress split. Shared with the phone drag-to-dismiss
        // gesture — see _dismissPane.
        _dismissPane(s.root, el.scope.primary)
      }
    }
  },

  // ── Content row: apps rail + mount target ─────────────────────────────
  // The rail slides OVER the page (absolutely positioned inside this box),
  // so the box is the positioning + clipping context. The Slot is this row's
  // flex item and the keep-alive hosts are the Slot's — the same sizing lever
  // as before, one level down (_paneOf and slotPaneKeyFor walk by pane stamp,
  // so every pane reach stays correct). The Slot is the hosted pages'
  // stacking context; a host scrolls, so it may never become one (see the
  // Slot and _hostDef notes — the iOS fixed-layer rule).
  Body: {
    flex: '1 1 0',
    minWidth: '0',
    minHeight: '0',
    width: '100%',
    flow: 'x',
    overflow: 'hidden',
    position: 'relative',
    // (The person's sidebar choice is the page's own now — shared
    // functions/pageSidebar.js, one key per page: a hidden AppSidebar folds
    // itself down to its reopen tab, in a window exactly as at its own URL,
    // so this box carries no rule of its own.)
    // Container breakpoints follow this pane's width, including split
    // views, independently of the browser viewport.
    // Reset the module-rail clearance for everything INSIDE the slot: the
    // slot pane itself already insets left by the rail's width (the
    // .isSlotFullscreen class), so module pages that pad-left by the same
    // var on ROUTE mounts must resolve it to 0 here or they double-inset.
    style: {
      containerType: 'inline-size',
      containerName: 'app-surface',
      '--module-rail-clear': 'calc(var(--spacing-A) * 0)'
    },

    AppsRail: _slotRail(SLOT_KEY),

    // The mount target — keep-alive per pane, el.set(pageDef).
    Slot: {
      // On the split pane, collapse the mount target while the placeholder shows
      // (no route yet) so the EmptyState fills the pane. The primary Slot is
      // always present — its onUpdate owns the ?page= URL sync.
      hide: (el, s) => (el.scope.placeholderKey ? !s.root?.[el.scope.SLOT_KEY] : false),
      // A REAL BOX, AND THE PANE'S ONE STACKING CONTEXT FOR ITS PAGES. The key
      // renders a `<slot>` element, whose UA style is `display: contents` — so
      // until 2026-10-05 this generated no box and the keep-alive hosts were the
      // content row's flex items. It now declares its display and carries
      // WS-SLOT-HOST-ISOLATES-STACKING-1's isolation (a module page's own z 20 /
      // z 30 layers stay under the apps rail, z 3, this box's positioned
      // sibling). The isolation moved here from the host because the host
      // SCROLLS, and iOS Safari clips every fixed layer inside a scroller that
      // is a stacking context (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). So this box
      // never scrolls — no `overflow` — and the hosts below never isolate.
      // Its box is the content row's (flex 1 1 0, full width, no padding), so
      // every host keeps its size.
      display: 'flex',
      flex: '1 1 0',
      minWidth: '0',
      minHeight: '0',
      width: '100%',
      // A flat prop: `isolation` is in css-in-props' property list, and the
      // shell root's own flat `isolation` computes `isolate` live (dev,
      // 2026-10-05). The host's old `style` route answered a socials 1.7.1
      // reading of `auto` on an imperatively created host.
      isolation: 'isolate',
      // No own ground since the 2026-09-01 gray-pane pass: the PANE's
      // 'pane-ground' gray IS the sheet's ground, and the symmetric gutter
      // below lets it frame the mounted page. A painted 'document' fill here
      // would cover the gray.
      // The mounted page root must fill the space REMAINING under the pane
      // Header — not the whole pane.
      //
      // History (when this element was `display: contents` and the content row
      // did not exist yet): the page root was a direct flex child of
      // PanePrimary. Page roots carry
      // `min-height: 100%`, which then resolved against the PANE (794px) rather
      // than the leftover space, so every slot page laid out as
      // `Header (47px) + page (794px) = 841px` inside a 794px pane whose overflow
      // is hidden — silently clipping the bottom ~47px of EVERY page opened in
      // the sheet, with no way to scroll to it.
      //
      // Sizing the child as a flex item makes it take the remaining height, at
      // which point the page's own scroll container works as designed. Anchored
      // through the slot scope, the same lever the sidebar rules below use,
      // because the page def can't be edited from here (`el.set` re-creates it).
      //
      // `[data-key]` rather than `*` is load-bearing: the page root's own
      // `min-height: 100%` comes from an atomic CLASS, so a `& > *` selector ties
      // it on specificity (0,1,0) and the winner falls to stylesheet order — it
      // lost, and the page stayed pinned to full pane height. The attribute
      // selector takes it to (0,2,0) so the override is deterministic. Every
      // mounted page root carries a data-key.
      // margin 'Y' = the symmetric content gutter (2026-09-01 per Nika
      // "keep paddings symmetrical for all pageslot content"): the host
      // sheet insets equally on all four sides — Y under the header, Y at
      // both edges (matching the header's own '0 Y' inset), Y at the
      // bottom. background transparent = ONE ground: this (0,2,0) rule
      // outweighs a page root's own atomic ground class, so every page in
      // the slot sits directly on the pane's stage-chain ground instead of
      // painting a second document-family sheet over it ("keep one bg" /
      // "very consistent"). Page content SURFACES (cards, grids) keep
      // their own fills. Phone keeps the edge-to-edge sheet (the @mobileL
      // block below zeroes the margin).
      '& > [data-key]': {
        flex: '1 1 0',
        minWidth: '0',
        minHeight: '0',
        maxWidth: 'calc(100% - var(--spacing-Y) * 2)',
        margin: 'Y',
        background: 'transparent'
      },
      // The page's INNER 'content' wrappers (AppPage / the router mount
      // convention) paint the stage chain for STANDALONE renders — inside
      // the pane that re-painted gray5 patches over the unified sheet
      // (measured 2026-09-01: a 1896x779 stage-colored wrapper behind
      // CalMain). Inside the slot, grounds belong to the pane alone.
      '& [data-key="content"]': { background: 'transparent' },
      // Split panes need the same width bounds as phones: wide tables and
      // unbroken text must scroll inside their host, not widen the frame.
      // Only the phone's edge-to-edge content removes the desktop gutter.
      '@mobileL': { '& > [data-key]': { maxWidth: '100%', margin: '0' } },
      // Hide the mounted page's OWN nav rail inside the sheet — the sheet is a
      // focused modal and these rails duplicate switchers owned by their canvas
      // surface. The rail can't be stripped from the pre-extended page def via
      // el.set (key-based auto-extend re-creates it — verified 2026-06-15), so a
      // scope-anchored descendant rule is the reliable lever.
      '& [data-key="TicketsSidebar"]': { display: 'none' },
      '& [data-key="ResolutionsSidebar"]': { display: 'none' },
      // workspaceProject is a REAL dependency of the mount: module routes only
      // resolve once the loader injects the module's pages. Subscribing to it
      // makes the mount order-independent — if the ?page= restore (or any
      // opener) sets the slot key BEFORE injection, the injection tick retries
      // _mountPane and the sheet fills the moment the def exists (previously a
      // pre-injection open stayed an empty sheet forever: the re-apply write is
      // same-value → deduped, so nothing re-fired this effect).
      // `_minimizedSlots` re-fires the host sweep on dock closes / evictions;
      // `_slotNudge` re-fires it after a close choreography lands (the sweep
      // defers while the pane holds `_closing` so a fading sheet never blanks).
      subscribeTo: [SLOT_KEY, 'workspaceProject', '_minimizedSlots', '_slotNudge'],

      // Back/forward reopens (or closes) the slot the URL describes — the read
      // half of `_writeSlotUrl`'s pushState in onCreate below.
      //
      // This was a raw `window.addEventListener('popstate', …)` latched on a
      // `window.__slotPopstateWired` GLOBAL — a Rule 64 window write AND an
      // anonymous handler nothing could ever remove. The latch existed only
      // because the registration had no owner; a declared prop has one, so
      // both are gone. PRIMARY still owns the URL: the secondary pane must
      // never sync it, which is why the guard moved into the handler rather
      // than into the registration.
      onWindowPopstate: (ev, el, s) => {
        if (!el.scope?.primary) return
        _syncSlotFromUrl(s.root)
      },

      onCreate: (el, s) => {
        // Primary only (re)affirms the URL on mount when a slot is ALREADY open.
        // NEVER write on the initial null mount — the slot is always mounted, so
        // an unconditional write would DELETE a ?page= param on page load before
        // the home onCreate reads it back, breaking reload-restore.
        if (el.scope.primary && s.root?._canvasSlot) _writeSlotUrl(s.root._canvasSlot, s.root)
        // The read half of the pushState above is DECLARED — see
        // `onWindowPopstate` below.
        // Baseline for onUpdate's edge-triggers below (URL sync + close
        // notify) — must mirror the mount-time value just acted on above, or
        // their FIRST update pass (often fired by the workspaceProject
        // subscription above, before any real slot change) reads as a
        // spurious transition.
        if (el.scope.primary) {
          el.__lastSlotPath = s.root?._canvasSlot || null
          if (el.__lastSlotPath) el.__slotTitleOwner = _routeTitleOwner(el)
        }
        // File the already-open route into this pane's history (reload restore
        // mounts with the slot key pre-set, so onCreate is its first sighting).
        _recordSlotHist(s, el.scope.SLOT_KEY, el)
        _mountPane(el, s, el.scope.SLOT_KEY)
        if (el.scope.primary) _adoptActive(el, s)
        _sweepHosts(el, s, el.scope.SLOT_KEY, el.scope.primary)
      },
      onUpdate: (el, s) => {
        if (el.scope.primary) {
          const path = s.root?._canvasSlot || null
          const was = el.__lastSlotPath
          el.__lastSlotPath = path
          if (path && !was) el.__slotTitleOwner = _routeTitleOwner(el)
          // URL sync on the SLOT_KEY's OWN transitions only — never merely
          // because a SIBLING subscription re-rendered this pane.
          // `workspaceProject` sits in subscribeTo above so a mid-boot module
          // publish retries `_mountPane` (see the comment on that array);
          // before this guard, that same publish re-ran this write on every
          // pass, and while a cold-load ?page= restore hadn't landed yet
          // (_canvasSlot still null across the publish) it read as "the user
          // just closed the slot" and deleted the boot ?page= param before
          // _restorePageParam (workspaceProjects.js) ever got a chance to
          // read it back (WS-MODULE-PAGE-DEEPLINK-DROPPED-ON-COLD-LOAD-1).
          // push — a room, a board, an item are each a real open the user
          // did; back steps through them (WS-MODULE-ROUTE-QUERY-NO-HISTORY-
          // ENTRY-1). An in-place query patch is not an open: the page stays
          // and only its route changes, so it REPLACES the entry (see
          // `_inPlaceQueryPatch`; Back must never land on the `?new=1` the
          // page already consumed). _writeSlotUrl itself still replaces on a
          // close (path falsy) or a no-op (URL unchanged) — see its own comment.
          if (path !== was) {
            _writeSlotUrl(path, s.root, { push: !_inPlaceQueryPatch(el, path, was) })
          }
          // The home callback owns its immersive close/reopen behavior. Every
          // dedicated route must also reclaim its current Helmet title: the
          // underlying page stays mounted, so Helmet does not run on close.
          // EDGE-TRIGGERED (2026-08-16): this hook used to fire on EVERY
          // update pass while the slot stayed null, so any root churn in the
          // closed window re-invoked it — which defeated one-shot guards
          // inside (measured: moduleGoHome's _slotSkipReopenOnce was
          // consumed by pass 1 and pass 2 ran the immersive reopen anyway).
          // Notify only on the non-null → null TRANSITION (`was` above is
          // the shared edge state the URL sync also reads).
          if (!path && was && s.root) {
            if (s.root._slotOnClose) s.root._slotOnClose()
            if (!s.root._canvasSlot) _restoreRouteTitle(el)
          }
        }
        // Per-pane history recorder — the one choke point every slot writer
        // flows through (see _recordSlotHist). Before the mount guard, so the
        // arrows' disabled state settles in the same pass as the route, and
        // so a re-mount's fresh host (stamped with the NEW route once its
        // apply lands) can never read as an in-place patch here.
        _recordSlotHist(s, el.scope.SLOT_KEY, el)
        _mountPane(el, s, el.scope.SLOT_KEY)
        // Un-park a restored route, then unmount whatever is neither active,
        // nor parked, nor stamped for the live workspace (see _sweepHosts).
        if (el.scope.primary) _adoptActive(el, s)
        _sweepHosts(el, s, el.scope.SLOT_KEY, el.scope.primary)
        // After the mount guard: a keep-alive re-open (mount skipped, helmet
        // silent) and a second-pane close both land here needing the primary's
        // title re-asserted.
        _syncSlotTitle(el, s.root)
      }
    },

    // Split-pane placeholder — the moment a split is armed (before an app is
    // picked) the empty second pane shows this instead of blank space, so the
    // split telegraphs (2026-07-16 per Nika "display placeholder section").
    // Reuses the house EmptyState; only the split pane ships it (placeholderKey).
    ...(placeholderKey
      ? {
          SplitPlaceholder: {
            extends: 'EmptyState',
            subscribeTo: [SLOT_KEY, placeholderKey],
            hide: (el, s) => !!s.root?.[el.scope.SLOT_KEY] || !s.root?.[el.scope.placeholderKey],
            flex: '1 1 0',
            minHeight: '0',
            icon: 'columns',
            // KEY on `heading` / `hintText` — the shared EmptyState resolves
            // both through polyglot (docs/I18N-CONVENTION.md §1.2).
            heading: 'canvas.pageSlot.placeholder.heading',
            hintText: 'canvas.pageSlot.placeholder.hint'
          }
        }
      : {})
  },

  // THE RESIZE HANDLE (the width block above): the sheet's right edge, a
  // focusable vertical separator with its value. A drag, the arrow keys (Shift
  // for a longer step), Home / End and Enter; a double-click returns to the
  // default. Only on the one floating window, and not where the sheet is
  // already the whole band (a tablet, a phone). The reset's focus ring shows
  // on keyboard focus; the strip tints on hover and focus.
  SheetResizeHandle: {
    hide: (el, s) => !el.scope.primary || !_isSheet(el, s.root),
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    width: 'Y',
    zIndex: '3',
    cursor: 'col-resize',
    touchAction: 'none',
    userSelect: 'none',
    background: 'transparent',
    transition: 'background-color A defaultBezier',
    ':hover': { background: 'selected' },
    ':focus-visible': { background: 'selected' },
    '@tabletS': { display: 'none' },
    role: 'separator',
    tabindex: '0',
    'aria-orientation': 'vertical',
    'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.resizeWindow'),
    'aria-valuemin': String(_SHEET_MIN_PCT),
    'aria-valuemax': '100',
    'aria-valuenow': (el, s) => String(Math.round(_sheetPct(s.root))),
    'aria-valuetext': (el, s) => `${Math.round(_sheetPct(s.root))}%`,
    onPointerdown: (e, el, s) => _sheetDragStart(e, el, s),
    onPointermove: (e, el, s) => _sheetDragMove(e, el, s),
    onPointerup: (e, el, s) => _sheetDragEnd(e, el, s, false),
    onPointercancel: (e, el, s) => _sheetDragEnd(e, el, s, true),
    onLostpointercapture: (e, el, s) => _sheetDragEnd(e, el, s, true),
    onKeydown: (e, el, s) => _sheetKey(e, el, s),
    onDblclick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      if (s.root) _saveSheetPct(el, s.root, null)
    }
  }
})

// ── The window's presentation (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1) ────────
//
// Nika's pick 10 A (2026-09-28, AI_ASSISTANT_SPEC §11): app pages open as an
// iPad SHEET above the composer dock — the aihouse pattern (templates/aihouse
// PropertyModal): a blurred veil, a card anchored above the dock with the dock
// reserved, full screen on a phone, closed by Escape (the shell keyboard
// registry presses the window's own X — shortcutsDefault closeAppWindow) or a
// press on the veil. Unlike aihouse, where the ask bar lives INSIDE the modal,
// the shell composer floats over the window, so the window is no modal
// dialog (see the note on the frame). The frame's band is unchanged (--slot-top to
// --slot-bottom, X2 gutters — every shell rule that moves it for a drawer or a
// dock still applies); a SINGLE pane in the default floating state is the
// person's share of that band, aligned to its leading edge (the width block
// above `_slotPane`). A split (two windows side by side) and the module presentations — fullscreen,
// the admin's expanded view, the rail inset, immersive members — keep the
// whole band exactly as before.
//
// One read for every consumer (the frame's classes and attributes, the veil,
// the Tab trap). Every dependency is read up front and unconditionally, so a
// factory that calls this re-runs on any of them — a short-circuit would drop
// a tracked read (the persona flip, a fullscreen toggle).
const _slotModes = (el, root) => {
  const fns = el.context?.functions || {}
  const primary = !!root?._canvasSlot
  const second = !!root?._canvasSlotSecond
  const arming = !!root?._slotSplitIntent
  const imm = typeof fns.moduleImmersiveActive === 'function' && !!fns.moduleImmersiveActive(root)
  const rail = typeof fns.moduleRailActive === 'function' && !!fns.moduleRailActive(root)
  const fsFn = fns.slotFullscreenActive
  const fullscreen =
    typeof fsFn === 'function'
      ? !!fsFn(root)
      : (!!root?._slotFullscreen || imm) && !second && !arming
  return { open: primary || second, split: second || arming, imm, rail, fullscreen }
}

// The veil closes the window only where the veil is the page behind a
// floating sheet. An immersive member's window IS their surface (its close is
// a route to the module landing, the X's job), and a fullscreen window leaves
// no page to press.
const _veilCloses = (el, root) => {
  const m = _slotModes(el, root)
  return m.open && !m.imm && !m.fullscreen
}

// Blur + dim the PAGE behind an open slot, so the glass panes read as a focused
// layer instead of competing with live content showing through.
//
// Deliberately NOT a child of PageSlot. The frame sets `zIndex: 1300`, which
// makes it a stacking context — so any descendant, at any z-index, paints above
// EVERYTHING below 1300, and that swept the app chrome into the blur along with
// the page (per Nika: "navbars should be above blur, we should blur only
// background stuff"). Mounted as its own shell-root layer instead.
//
// It sits at 20 — deliberately BELOW every piece of fixed chrome in the app,
// rather than merely below the header. The rule is "blur the page, keep the
// app": page content is normal-flow / z-auto (including the absolutely
// positioned gridstack widgets), while every chrome surface is fixed with an
// explicit z. One value below the lowest of those keeps them all sharp:
//
//   page content (normal flow, gridstack items, z-auto) …… BLURRED
//   THIS BACKDROP ……………………………………………………………………………… 20
//   CanvasSplitView 24 ………………………………………………………………… sharp
//   AI chat composer 60 · AiActionConfirmCard 95 ………… sharp  (per Nika)
//   CanvasRail 900 · CanvasOverlay 950 …………………………… sharp  (globalScope.js)
//   AppHeader 1100 · OnlineMembersDock 1150 ………………… sharp
//   PageSlot 1300 · Suggestions 1349 · ReasoningPill 1350  sharp
//
// backdrop-filter samples only what is painted behind the element, so the
// chrome above is untouched by construction, not by masking. Picking one z
// under the whole chrome band also means a new chrome layer inherits the right
// behaviour for free, instead of needing its own bump each time.
//
// Interactive since pick 10 A (see `_veilCloses` and the pointerEvents note
// below): a press on the veil closes the window, the aihouse sheet's
// dismissal. It was a click-through visual treatment before.
//
// Gated with `hide:`, the v3.14 reactive gate. A reactive `opacity` factory and
// a conditional-class truthy prop were both tried live as a PageSlot child and
// neither re-evaluated; `hide` + an explicit `subscribeTo` at shell-root scope
// is the pattern that works (same shape as OnlineMembersDock).
export const PageSlotBackdrop = {
  subscribeTo: ['_canvasSlot', '_canvasSlotSecond', '_slotSplitIntent', '_slotFullscreen'],
  hide: (el, s) => !(s.root?._canvasSlot || s.root?._canvasSlotSecond),
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  zIndex: '20',
  // A PRESS ON THE VEIL CLOSES THE WINDOW (pick 10 A — the aihouse sheet:
  // "Esc or the veil closes it"). The veil only covers the PAGE: every piece
  // of chrome — the header, the composer, the rails — sits above z 20 and
  // stays live, so the press that closes is a press on the page behind, never
  // on a control. Where the veil must not close (an immersive member's
  // window, a fullscreen one) it stays click-through as before.
  pointerEvents: (el, s) => (_veilCloses(el, s.root) ? 'auto' : 'none'),
  onClick: (e, el, s) => {
    if (!_veilCloses(el, s.root)) return
    e.preventDefault()
    _closeSlot(s.root)
  },
  // Tuned down over several passes (per Nika: "a bit more grayish" → "make more
  // lighter" → "maybe make it lighter" → "add more opacity so background is a
  // bit visible"). Two independent dials, and by the end the TINT was already
  // near nothing — gray7 (rgba(101,101,111)) at 0.12 — so the thing still
  // hiding the page was the BLUR, not the colour. Hence 4px rather than 8px:
  // at this radius the page behind stays legible in outline (you can see what
  // you left open) while the slot's glass panes still read as the focused
  // layer. Softening the blur is what actually makes the background visible;
  // dropping the tint alone would only have shifted its colour.
  //
  // Fixed-colour sweep: kept — this veils WHATEVER app page happens to be
  // open behind the slot (arbitrary, theme-unknown from here by
  // construction), not this component's own chrome. A neutral fixed tint
  // dims any page by a consistent amount regardless of its theme.
  //
  // THE WINDOW VEIL IS NOW A NAMED THEME (DESIGN_SPEC §6.1 "Window", §6.3):
  // brand 3.14.798's `backdropSoft` IS this hand-tuned veil byte for byte —
  // `scrimSoft` = gray7.12 in BOTH schemes + blur(4px) — so adopting it
  // changes nothing on screen in either scheme, and the tuning now lives in
  // the one place every non-blocking veil reads. Under reduced transparency
  // the blur goes (§6.5).
  theme: 'backdropSoft',
  '@reduceTransparency': { backdropFilter: 'none' }
}

// ── The page a window shows while its real page mounts ─────────────────────
//
// Pick 10 A (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1): "The sheet opens at once
// with a skeleton; the page fills in when it mounts." A page is prepared
// synchronously and a big one costs frames, and a lazy route first loads its
// chunk — so the window used to open EMPTY (or late) and fill in all at once.
// Now the keep-alive host is created the moment the route arrives, with THIS
// as its content, and the page replaces it after the first paint
// (`_mountPane`); a lazy route's placeholder shows the same skeleton until
// its chunk lands (workspace pages/lazyPage.js). The shape is the house
// page's: a title line over the body's rows, in the house page inset — built
// from the shared Skeleton rows (their pulse stops under reduced motion).
// Exported here so it rides the canvas eager slice with PageSlot itself: the
// shell can mount it before any lazy chunk exists.
export const PageSkeleton = {
  flow: 'y',
  gap: 'A',
  padding: 'B C',
  width: '100%',
  minWidth: '0',
  minHeight: '0',
  boxSizing: 'border-box',
  // A loading region: busy, and named for assistive technology.
  role: 'status',
  'aria-busy': 'true',
  'aria-label': (el) => el.call('polyglot', 'common.state.loading'),
  '@mobileL': { padding: 'A' },
  SkeletonRow_title: { width: '40%', height: 'B', marginBottom: 'Z' },
  SkeletonRow_1: { width: '60%' },
  SkeletonRow_2: { width: '100%' },
  SkeletonRow_3: { width: '85%' },
  SkeletonRow_4: { width: '70%' },
  SkeletonRow_5: { width: '90%' }
}

// ── The modal layer ────────────────────────────────────────────────────────
export const PageSlot = {
  // Fixed frame above every page surface — canvas rails + overlay chrome sit
  // at 900/950 (globalScope.js's "Canvas layering contract", both below the
  // platform AppHeader on purpose); ModalRoot (10500) and notifications stay
  // above. pointerEvents:none so the frame never blocks the page when a pane
  // is closed; panes re-enable.
  position: 'fixed',
  zIndex: '1300',
  pointerEvents: 'none',
  // Equal live-measured gaps: --slot-top = header-navbar bottom + --nav-gap,
  // --slot-bottom = composer top + --nav-gap (ResizeObserver-maintained in
  // app.js). Bare custom-property names — scratch wraps a bare --name in var()
  // exactly once (inline var()/calc() would re-wrap and void the rule).
  top: '--slot-top',
  bottom: '--slot-bottom',
  left: 'X2',
  right: 'X2',
  flow: 'x',
  gap: 'Z',

  // THE SHEET (pick 10 A — see `_slotModes`) is sized on the PANE, as a
  // share of this band (the width block above `_slotPane`): the frame itself
  // stays the band in every state — top, bottom and the X2 gutters, and every
  // shell rule that moves them for a drawer or a dock — so the sheet stays
  // anchored above the dock and every layer that reads the band is untouched.

  // NOT a modal dialog, on purpose. The composer floats OVER the window (the
  // shell's 1400 layer, canvas CanvasPromptTextarea F2: "so a person can
  // prompt over an open app"), and the shell keyboard registry treats a window
  // as a SURFACE, not an overlay (functions/shortcuts.js — `/` reaches the
  // composer, G-sequences navigate, Esc presses the window's own X through
  // closeAppWindow). `role="dialog"` / `aria-modal` would hide the composer
  // from assistive technology, trap Tab away from it and silence every
  // binding while focus sits in the window. So each pane stays a labelled
  // region (`_slotPane`), focus still moves in on open and back on close, and
  // Esc and a press on the veil close it.

  // Edge-to-edge fullscreen (single-pane primary). A conditional CLASS — not
  // reactive layout factories, which garbage when re-run on a class-toggle
  // re-render in v3.14 (verified 2026-07-11).
  isSlotFullscreen: (el, s) => {
    if (_slotOnPhone(s.root)) return false
    // Immersive module chrome (workspace module manifest `chrome.immersive`,
    // resolved by the shell's moduleImmersiveActive): these members have no
    // shell header/composer, so the slot runs edge-to-edge by default — the
    // module page IS the app surface. Reactivity rides the _canvasSlot
    // re-render (the immersive landing is opened in the same pass that
    // publishes workspaceProject).
    //
    // A rail alone (chrome.rail, admins included) deliberately does NOT
    // force fullscreen anymore: for admins the shell navbar + composer are
    // still live chrome, and an edge-to-edge sheet ran UNDER the navbar and
    // let the composer float over slot content (measured live 2026-08-14,
    // per Nika "fix sidebar + pageslot positions when signed with admin so
    // it does not overlap them"). Admins keep the floating card, inset
    // beside the rail by the .isSlotRailInset class below; immersive
    // members (no navbar/composer) keep edge-to-edge.
    const imm = el.context?.functions?.moduleImmersiveActive
    const immersive = typeof imm === 'function' && imm(s.root)
    const fsFn = el.context?.functions?.slotFullscreenActive
    const active =
      typeof fsFn === 'function'
        ? fsFn(s.root)
        : (!!s.root?._slotFullscreen || immersive) &&
          !s.root?._canvasSlotSecond &&
          !s.root?._slotSplitIntent
    // Edge-to-edge for immersive chrome AND for an EXPLICIT user toggle
    // (BF-PAGESLOT-EXPAND-1: with edge-to-edge reserved to immersive, an
    // admin's expand click changed the slot by 12 TOTAL pixels — gutters
    // only — which Nika read, correctly, as "expand does not work". The
    // DEFAULT expanded state keeps the chrome clearances below; the
    // explicit toggle is the user's own call to cover the chrome, which
    // the 2026-08-14 no-overlap complaint — about a FORCED structural
    // fullscreen — never prohibited).
    return (active && immersive) || (s.root?._slotFullscreen === true && active)
  },
  // Left edge insets by the immersive module rail's live width
  // (--module-rail-clear, 0 whenever no rail renders) — the slot opens
  // BESIDE the module's persistent nav, never under it.
  '.isSlotFullscreen': {
    // Nika 2026-08-16: the expanded view keeps LITTLE top/bottom offsets —
    // never flush to the screen edge. Z is the small desk step; the rail
    // matches it (app.js ModuleRailHost base geometry) so the two stay on
    // one band.
    top: 'Z',
    // SHELL-ASSISTANT-RESERVE-1: a `.case` block lands INLINE, so these
    // edges outrank the shell's attribute-keyed `[data-key="PageSlot"]`
    // dock rules — the sheet slid UNDER the docked AI drawer. Add the
    // shell-published per-side inset (`--assistant-inset-*`, 0px when
    // nothing is docked on that side, the clamped drawer reserve when it
    // is) so the slot always clears the drawer in every state.
    left: 'calc(var(--module-rail-clear, 0px) + var(--assistant-inset-left, 0px))',
    right: 'var(--assistant-inset-right, 0px)',
    bottom: 'Z'
  },
  // Expanded (B1, tickets/bellforge.md): the ADMIN's fullscreen — maximal
  // within live chrome. Vertical band stays --slot-top/--slot-bottom (navbar
  // + composer remain usable, per the 2026-08-14 no-overlap contract); the
  // horizontal gutters drop, flush to the module rail on the left and the
  // viewport on the right. Module workspaces open in THIS state by default
  // (slotFullscreenActive's tri-state default); the header toggle records an
  // explicit choice that persists per workspace.
  // Rail-edge frame (2026-09-02, bellforge lane relaying Nika "left is
  // correct margin — right and bottom and top is not, fix proportions those
  // in pageslot"): in the fullscreen state WITH a module rail the slot's
  // left edge sits one Z off the rail while its right edge touched the
  // window (measured on app.bellforge.eu: rail band x 10..262, slot left
  // 272, right 0, top/bottom Z). The frame now reads Z on all four sides:
  // right gains the same Z the rail keeps on the left (plus the assistant
  // inset, as before). Declared AFTER '.isSlotFullscreen' so it wins the
  // cascade at equal specificity; no-rail fullscreen keeps right 0.
  isSlotRailEdge: (el, s) => {
    if (_slotOnPhone(s.root)) return false
    const railFn = el.context?.functions?.moduleRailActive
    if (!(typeof railFn === 'function' && railFn(s.root))) return false
    return !!el.isSlotFullscreen?.(el, s)
  },
  '.isSlotRailEdge': {
    right: 'calc(var(--spacing-Z) + var(--assistant-inset-right, 0px))'
  },
  isSlotExpanded: (el, s) => {
    if (_slotOnPhone(s.root)) return false
    const imm = el.context?.functions?.moduleImmersiveActive
    const immersive = typeof imm === 'function' && imm(s.root)
    const fsFn = el.context?.functions?.slotFullscreenActive
    const active =
      typeof fsFn === 'function'
        ? fsFn(s.root)
        : !!s.root?._slotFullscreen && !s.root?._canvasSlotSecond && !s.root?._slotSplitIntent
    // The chrome-cleared band is the DEFAULT expanded state only — an
    // explicit toggle escalates to the edge-to-edge class above.
    return active && !immersive && s.root?._slotFullscreen !== true
  },
  // S4 (tickets/sonnet.md, NIKA BATCH 2026-08-16): top was --slot-top (live
  // AppHeader-measured: --nav-clear + --nav-gap) while every sidebar this
  // view sits beside — ProjectCanvasSidebar, CanvasViewSidebar, InfraSidebar,
  // CanvasV2CodeEditor, VersionDetailPanel, VersionHistoryPanel — uses
  // --shell-top (px-pinned @property: spacing-C1 + 3px + safe-area-inset-top).
  // Two different mechanisms computing what's supposed to be the same edge
  // never guaranteed the same pixel value. Expanded is the ADMIN's flush,
  // beside-the-rail view specifically, so it now shares the sidebars' own
  // top var instead of the floating-card's live-measured one (the BASE
  // isSlotFullscreen/default states keep --slot-top/-bottom untouched — that
  // symmetry is deliberate for the floating card, unrelated to this bug).
  '.isSlotExpanded': {
    top: '--shell-top',
    // Same inline-case rule as `.isSlotFullscreen` above: keep clear of a
    // docked AI drawer on either side (SHELL-ASSISTANT-RESERVE-1) — and of
    // the navbar where the person put it on a side (`--nav-inset-*`, 0px at
    // the top; the shell's "THE NAVBAR'S PLACE" rules). max() on the left: a
    // module rail beside a left navbar already reaches past it.
    left: 'calc(max(var(--module-rail-clear, 0px), var(--nav-inset-left, 0px)) + var(--assistant-inset-left, 0px))',
    right: 'calc(var(--assistant-inset-right, 0px) + var(--nav-inset-right, 0px))',
    bottom: '--slot-bottom'
  },
  // Persistent module rail WITHOUT fullscreen (the admin default): the
  // floating card keeps its navbar/composer clearances (--slot-top/-bottom)
  // and only the LEFT edge moves — beside the rail with the same X2 gutter
  // the right edge keeps, so the card never sits under the rail ("pageslots
  // open right to the sidebar", overlap-free for admins).
  isSlotRailInset: (el, s) => {
    if (_slotOnPhone(s.root)) return false
    const railFn = el.context?.functions?.moduleRailActive
    if (!(typeof railFn === 'function' && railFn(s.root))) return false
    const imm = el.context?.functions?.moduleImmersiveActive
    const immersive = typeof imm === 'function' && imm(s.root)
    const fsFn = el.context?.functions?.slotFullscreenActive
    const fullscreen =
      typeof fsFn === 'function'
        ? fsFn(s.root)
        : (!!s.root?._slotFullscreen || immersive) &&
          !s.root?._canvasSlotSecond &&
          !s.root?._slotSplitIntent
    return !fullscreen
  },
  '.isSlotRailInset': {
    // + the left-dock inset (SHELL-ASSISTANT-RESERVE-1) — the right edge in
    // this state is the base X2 gutter, which the shell's dock rule outranks.
    left: 'calc(var(--module-rail-clear, 0px) + var(--spacing-X2) + var(--assistant-inset-left, 0px))'
  },

  // ── Phone: the frame becomes an iOS sheet ────────────────────────────────
  //
  // FULL-BLEED — left/right/bottom 0 (the X2 chrome gutters are a desktop
  // island idea; a phone sheet owns the screen edge) and the composer
  // clearance is deliberately SUSPENDED: --slot-bottom exists to keep a
  // floating card off the s1m0ne composer, but a sheet is allowed to cover it,
  // the same way an iOS sheet covers the tab bar. Top keeps _PHONE_TOP so a
  // strip of the scrimmed app behind stays visible (see SheetReveal) — that
  // sliver is what makes it read as a layer rather than a navigation.
  //
  // Emitted as a compound rule, so it outranks the flat top/left/right/bottom
  // above at ≤768 by insertion order (@symbo.ls/css emits flat first). The
  // nested `&.isSlotFullscreen` re-states it at the conditional class's own
  // specificity so an edge-to-edge toggle carried in from a wider viewport
  // cannot re-flatten the reveal strip: on a phone the sheet is already
  // fullscreen, so the toggle has nothing left to add.
  '@mobileL': {
    top: _PHONE_TOP,
    left: '0',
    right: '0',
    bottom: '0',
    '&.isSlotFullscreen': { top: _PHONE_TOP, left: '0', right: '0', bottom: '0' },
    // The expanded (admin) geometry collapses to the same phone sheet — a
    // phone sheet is already maximal, and --slot-top/--slot-bottom belong to
    // the desktop chrome band.
    '&.isSlotExpanded': { top: _PHONE_TOP, left: '0', right: '0', bottom: '0' },
    // The rail-inset class must not survive onto the phone sheet either —
    // full-bleed wins for the same reason as the fullscreen re-statement.
    '&.isSlotRailInset': { left: '0' }
  },

  // Section-nav-away dismissal for both panes (ticket 6a32d411). `_slotSplitIntent`
  // is here so the container re-renders when a split is armed/cancelled — the
  // placeholder pane appears/collapses on that signal.
  // `_isPhoneViewport`: the four state blocks above stand down on a phone.
  subscribeTo: [
    'currentPath',
    '_canvasSlot',
    '_canvasSlotSecond',
    '_slotSplitIntent',
    '_isPhoneViewport'
  ],
  onCreate: _dismissStaleSlot,
  onUpdate: _dismissStaleSlot,

  // The reveal strip's scrim — a light veil over the band of app left visible
  // above the phone sheet, so the strip reads as the layer BEHIND rather than a
  // hole punched in the screen. Only the strip is veiled; the page below it is
  // already handled by PageSlotBackdrop.
  //
  // Its own element rather than a `:before` on the frame or a pane, for two
  // reasons: (1) `hide` + `subscribeTo` is the gate proven to re-evaluate at
  // shell-root scope here (a conditional-class effect on a plain div is not —
  // see the SplitBtn note above), and (2) a pane carries a transform while it
  // animates or a finger drags it, which would make it the containing block for
  // a fixed child and drag the veil along (and clip it against the pane's
  // `overflow: hidden`). As a sibling INSIDE the frame's stacking context it
  // still paints above the app chrome, and it stays put while the sheet moves.
  // position:fixed keeps it out of the frame's flex flow.
  SheetReveal: {
    subscribeTo: ['_canvasSlot', '_canvasSlotSecond'],
    hide: (el, s) => !(s.root?._canvasSlot || s.root?._canvasSlotSecond),
    display: 'none',
    '@mobileL': { display: 'block' },
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    height: _PHONE_TOP,
    // Fixed-colour sweep: kept — same "veils arbitrary page content behind
    // the slot" contract as PageSlotBackdrop above.
    background: 'black.25',
    pointerEvents: 'none'
  },

  // Parked-apps chips (canvas/components/MinimizedDock.js). Lives INSIDE the
  // frame so it rides the slot layer's stacking context with no extra shell
  // mount; declared BEFORE the panes so the phone full-bleed sheet paints
  // ABOVE the chips by DOM order alone.
  MinimizedDock: {},

  PanePrimary: _slotPane('_canvasSlot', { primary: true }),
  PageSlotSplitHandle: {},
  PaneSecond: _slotPane('_canvasSlotSecond', { placeholderKey: '_slotSplitIntent' })
}

// Split sizing belongs to the frame, never to app state or the route. Updating
// just this number keeps both live hosts, histories, editors and scroll intact.
const _splitRatio = (root) => {
  const value = root?._slotSplitRatio
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(30, Math.min(70, value))
    : 50
}

export const PageSlotSplitHandle = {
  role: 'separator',
  tabindex: '0',
  'aria-orientation': 'vertical',
  'aria-controls': 'page-slot-pane-_canvasSlot page-slot-pane-_canvasSlotSecond',
  'aria-label': (el) => el.call('polyglot', 'canvas.pageSlot.resizeSplit'),
  'aria-valuemin': '30',
  'aria-valuemax': '70',
  'aria-valuenow': (el, s) => _splitRatio(s.root),
  title: (el) => el.call('polyglot', 'canvas.pageSlot.resizeSplitHint'),
  subscribeTo: ['_canvasSlot', '_canvasSlotSecond', '_slotSplitIntent', '_slotSplitRatio'],
  hide: (el, s) => !s.root?._canvasSlot || !(s.root?._canvasSlotSecond || s.root?._slotSplitIntent),
  '@splitBelowFloor': { display: 'none' },
  width: 'Z',
  minWidth: 'Z',
  margin: '0 -Y',
  flex: '0 0 auto',
  pointerEvents: 'auto',
  cursor: 'col-resize',
  touchAction: 'none',
  align: 'center',
  round: 'radiusPill',
  background: 'transparent',
  ':hover': { background: 'hover' },
  Grip: { width: 'W', height: 'C', round: 'radiusPill', background: 'caption' },
  onPointerdown: (e, el, s) => {
    if (e.button !== 0 || !s.root) return
    const rect = el.parent?.node?.getBoundingClientRect()
    if (!rect?.width) return
    e.preventDefault()
    e.stopPropagation()
    el._splitDrag = { x: e.clientX, width: rect.width, ratio: _splitRatio(s.root), id: e.pointerId }
    el.node?.setPointerCapture?.(e.pointerId)
  },
  onPointermove: (e, el, s) => {
    const drag = el._splitDrag
    if (!drag || !s.root || e.pointerId !== drag.id) return
    const ratio = Math.max(30, Math.min(70, drag.ratio + ((e.clientX - drag.x) / drag.width) * 100))
    s.root.update({ _slotSplitRatio: Math.round(ratio * 10) / 10 }, { preventFetch: true })
  },
  onPointerup: (e, el) => {
    const drag = el._splitDrag
    el._splitDrag = null
    if (drag && el.node?.hasPointerCapture?.(drag.id)) el.node.releasePointerCapture(drag.id)
  },
  onPointercancel: (e, el, s) => {
    const drag = el._splitDrag
    el._splitDrag = null
    if (drag) s.root.update({ _slotSplitRatio: drag.ratio }, { preventFetch: true })
  },
  onDblclick: (e, el, s) => {
    e.preventDefault()
    s.root.update({ _slotSplitRatio: 50 }, { preventFetch: true })
  },
  onKeydown: (e, el, s) => {
    let ratio = _splitRatio(s.root)
    const step = e.shiftKey ? 10 : 2
    if (e.key === 'ArrowLeft') ratio -= step
    else if (e.key === 'ArrowRight') ratio += step
    else if (e.key === 'Home') ratio = 30
    else if (e.key === 'End') ratio = 70
    else if (e.key === 'Enter') ratio = 50
    else if (e.key === 'Escape' && el._splitDrag) {
      ratio = el._splitDrag.ratio
      const id = el._splitDrag.id
      el._splitDrag = null
      if (el.node?.hasPointerCapture?.(id)) el.node.releasePointerCapture(id)
    } else return
    e.preventDefault()
    e.stopPropagation()
    s.root.update({ _slotSplitRatio: Math.max(30, Math.min(70, ratio)) }, { preventFetch: true })
  }
}
